import {
  BuildingOffice2Icon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import {
  CheckIcon,
  FilePlus2Icon,
  FilesIcon,
  FolderOpenIcon,
  Loader2Icon,
  ScissorsIcon,
  UploadIcon,
  XIcon,
} from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useUploadManagerUnified } from '@/components/documents-v2/hooks/useUploadManagerUnified';
import { PdfPageExtractorModal } from '@/components/pdf-extraction';
import { DocumentSelector } from '@/components/treatment/v3/request/package-edit/DocumentSelector';
import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogBody,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import {
  Z_INDEX_MODAL_DEEPLY_NESTED,
  Z_INDEX_MODAL_NESTED,
} from '@/components/ui/zIndex';
import { cn } from '@/lib/utils';
import {
  useGetBlobDetailQuery,
  useGetBlobsQuery,
} from '@/services/api/blobService';
import { useGetCollectionTreeQuery } from '@/services/api/collectionService';
import { useGetFirmQuery } from '@/services/api/firmsService';
import { enhancedApi as adminTemplatesApi } from '@/services/api/generated/adminTemplatesService';
import type { ReadTemplateSummary } from '@/services/api/generated/templatesV2Service';
import { useTemplateListQuery } from '@/services/api/generated/templatesV2Service';
import type {
  CreatePackageComponent,
  PackageComponentSchema,
  StaticDocumentTag,
} from '@/services/api/hipaaPackageService';
import {
  useAddComponentMutation,
  useRemoveComponentMutation,
} from '@/services/api/hipaaPackageService';
import { useGetMatterDetailsQuery } from '@/services/api/mattersService';
import {
  type EditorConfig,
  useConvertBlobToDocxMutation,
  useLazyGetBlobEditorConfigQuery,
  useLazyGetHipaaPackageComponentEditorConfigQuery,
  useLazyGetTemplateEditorConfigQuery,
} from '@/services/api/onlyofficeService';
import type { Blob as MatterBlob } from '@/services/types/blob-types';
import { MedRecsBrandingMode } from '@/services/types/firms-types';

import { useRequestPage } from '../contexts/RequestPageContext';
import { StaticDocumentLabels } from '../package-edit/constants';
import { BlobDocumentPreview } from '../package-view/BlobDocumentPreview';
import { MemoizedIframePreview } from '../package-view/MemoizedIframePreview';
import {
  getSignedAuthorizationSelection,
  setSignedAuthorizationSelection,
} from '../utils/signedAuthorizationCache';
import { MatterSignatureCaptureModal } from './MatterSignatureCaptureModal';
import {
  isMatterSignatureSourceBlob,
} from './matterSignatureUtils';
import {
  getPackageComponentTitle,
  getVisiblePackageComponents,
  isOnlyOfficeEditablePackageComponent,
} from './utils';

interface InlineOnlyOfficeEditorProps {
  editorConfig: EditorConfig;
  onDocumentStateChange?: (hasChanges: boolean) => void;
}

interface DocumentPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialComponentId?: string;
  onPotentialOnlyOfficeSave?: (
    editedComponents: Array<{
      componentId: string;
      modifiedAt: string | null;
      waitMode: 'grace' | 'strict';
    }>,
  ) => void;
  initialAddDocumentKind?: AddDocumentKind;
}

type ModalTabState =
  | { type: 'component'; componentId: string }
  | { type: 'previous-package' }
  | { type: 'add' };

type AddDocumentKind =
  | 'coversheet'
  | 'authorization'
  | 'matter_document'
  | 'static_document'
  | 'manual_upload';
type PdfImportMode = 'as_pdf' | 'convert_to_docx';
type AuthorizationMode = 'standard_docx' | 'pdf_merge';
type TemplateLibraryKey = 'lighthouse' | 'global' | 'firm';

interface TemplateOption {
  id: string;
  name: string;
  category: 'HIPAA_COVERSHEET' | 'HIPAA_AUTHORIZATION' | 'FIRM_AUTHORIZATION';
  format: 'DOCX' | 'PDF' | 'HTML' | 'SMS' | 'EXTERNAL';
  library: TemplateLibraryKey;
  isDefault: boolean;
  previewRenderUrl?: string | null;
}

interface StaticDocumentOption {
  tag: StaticDocumentTag;
  description: string;
  previewUrl: string | null;
}

type PackageComponentWithModifiedAt = PackageComponentSchema & {
  modified_at?: string | null;
};

const INLINE_EDITOR_ID = 'hipaa-package-onlyoffice-editor';
const BACKEND_HOST = (
  import.meta.env.VITE_APP_DJANGO_BACKEND_HOST as string | undefined
)?.replace(/\/$/, '');
const DEFAULT_DOCX_LIBRARY_ORDER: TemplateLibraryKey[] = [
  'lighthouse',
  'global',
  'firm',
];
const DOCUMENT_CARD_BASE =
  'rounded-[20px] border bg-white shadow-[0_10px_30px_rgba(15,23,42,0.04)]';
const COMPACT_PREVIEW_HEIGHT = 'h-[400px] xl:h-[480px]';
const COMPACT_SIDEBAR_WIDTH = 'w-[260px] xl:w-[288px]';
const SHORT_VIEWPORT_HEIGHT = 950;

const buildBackendUrl = (path: string): string => {
  if (BACKEND_HOST) {
    return `${BACKEND_HOST}${path}`;
  }

  if (typeof window !== 'undefined') {
    return new URL(path, window.location.origin).toString();
  }

  return path;
};

const InlineOnlyOfficeEditor = ({
  editorConfig,
  onDocumentStateChange,
}: InlineOnlyOfficeEditorProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const editorInstanceRef = useRef<unknown>(null);
  const onDocumentStateChangeRef = useRef(onDocumentStateChange);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    onDocumentStateChangeRef.current = onDocumentStateChange;
  }, [onDocumentStateChange]);

  const initEditor = useCallback(() => {
    const { DocsAPI } = window as unknown as Record<string, unknown>;
    if (!DocsAPI || !containerRef.current) return false;

    try {
      const configWithSize = {
        ...JSON.parse(JSON.stringify(editorConfig.config)),
        height: '100%',
        width: '100%',
        events: {
          onDocumentStateChange: (event: { data?: boolean }) => {
            onDocumentStateChangeRef.current?.(Boolean(event.data));
          },
        },
      };
      const api = DocsAPI as {
        DocEditor: new (
          id: string,
          config: Record<string, unknown>,
        ) => unknown;
      };

      editorInstanceRef.current = new api.DocEditor(
        INLINE_EDITOR_ID,
        configWithSize,
      );
      setLoading(false);
      return true;
    } catch (err) {
      setError(`Failed to initialize editor: ${err}`);
      setLoading(false);
      return false;
    }
  }, [editorConfig]);

  useEffect(() => {
    let isMounted = true;

    const scriptId = 'onlyoffice-api-script';
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;

    const tryInit = () => {
      if (!isMounted) return;

      if (!initEditor()) {
        setTimeout(() => {
          if (!isMounted) return;
          if (!initEditor()) {
            setError(
              `OnlyOffice Document Server not available at ${editorConfig.document_server_url}`,
            );
            setLoading(false);
          }
        }, 1000);
      }
    };

    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.src = `${editorConfig.document_server_url}/web-apps/apps/api/documents/api.js`;
      script.async = true;
      script.onload = () => tryInit();
      script.onerror = () => {
        if (!isMounted) return;
        setError(
          `Failed to load OnlyOffice API from ${editorConfig.document_server_url}`,
        );
        setLoading(false);
      };
      document.body.appendChild(script);
    } else {
      setTimeout(tryInit, 300);
    }

    const containerElement = containerRef.current;

    return () => {
      isMounted = false;

      if (editorInstanceRef.current) {
        try {
          (
            editorInstanceRef.current as {
              destroyEditor?: () => void;
            }
          ).destroyEditor?.();
        } catch {
          // Ignore disposal failures from the embedded editor.
        }
      }

      editorInstanceRef.current = null;

      if (containerElement) {
        containerElement.innerHTML = '';
      }

      const oldScript = document.getElementById(scriptId);
      if (oldScript) {
        oldScript.remove();
      }
    };
  }, [editorConfig, initEditor]);

  if (error) {
    return (
      <div className="flex h-full items-center justify-center p-4">
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="relative flex h-full min-h-0 flex-1 flex-col">
      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white">
          <div className="flex flex-col items-center gap-2">
            <Loader2Icon className="h-6 w-6 animate-spin text-zinc-500" />
            <span className="text-sm font-medium text-zinc-500">
              Loading editor...
            </span>
          </div>
        </div>
      )}
      <div
        ref={containerRef}
        id={INLINE_EDITOR_ID}
        className="min-h-0 flex-1"
        style={{ display: loading ? 'none' : 'block' }}
      />
    </div>
  );
};

const isDocumentBlob = (blob: MatterBlob): boolean => {
  const filename = (blob.original_filename || blob.name || '').toLowerCase();
  return (
    filename.endsWith('.pdf') ||
    filename.endsWith('.docx') ||
    filename.endsWith('.doc')
  );
};

const isPdfBlob = (blob: MatterBlob): boolean => {
  const filename = (blob.original_filename || blob.name || '').toLowerCase();
  return filename.endsWith('.pdf');
};

const isWordBlob = (blob: MatterBlob): boolean => {
  const filename = (blob.original_filename || blob.name || '').toLowerCase();
  return filename.endsWith('.docx') || filename.endsWith('.doc');
};

const isPdfFile = (file: Pick<File, 'name' | 'type'>): boolean =>
  file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

const isWordFile = (file: Pick<File, 'name' | 'type'>): boolean =>
  file.type ===
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
  file.type === 'application/msword' ||
  file.name.toLowerCase().endsWith('.docx') ||
  file.name.toLowerCase().endsWith('.doc');

const normalizeTemplate = (
  template: Pick<
    ReadTemplateSummary,
    | 'id'
    | 'name'
    | 'display_name'
    | 'category'
    | 'format'
    | 'is_default'
    | 'is_generic_template'
    | 'preview_render_url'
  > & { firm_id?: string | null },
  showLighthouseLibrary: boolean,
): TemplateOption | null => {
  if (
    template.category !== 'HIPAA_COVERSHEET' &&
    template.category !== 'HIPAA_AUTHORIZATION' &&
    template.category !== 'FIRM_AUTHORIZATION'
  ) {
    return null;
  }

  let library: TemplateLibraryKey = 'global';
  if (showLighthouseLibrary && template.is_generic_template) {
    library = 'lighthouse';
  } else if (template.firm_id) {
    library = 'firm';
  }

  return {
    id: String(template.id),
    name: template.display_name || template.name,
    category: template.category,
    format: template.format,
    library,
    isDefault: template.is_default,
    previewRenderUrl: template.preview_render_url,
  };
};

const sortTemplates = (templates: TemplateOption[]) =>
  [...templates].sort((left, right) => {
    if (left.isDefault !== right.isDefault) {
      return left.isDefault ? -1 : 1;
    }
    return left.name.localeCompare(right.name);
  });

const pickPreferredLibrary = (
  libraries: TemplateLibraryKey[],
): TemplateLibraryKey | null =>
  DEFAULT_DOCX_LIBRARY_ORDER.find((library) => libraries.includes(library)) ??
  libraries[0] ??
  null;

const pickPreferredTemplate = (
  templates: TemplateOption[],
): TemplateOption | null => sortTemplates(templates)[0] ?? null;

const getLibraryLabel = (library: TemplateLibraryKey) => {
  switch (library) {
    case 'lighthouse':
      return 'Project Lighthouse';
    case 'global':
      return 'Global Templates';
    case 'firm':
      return 'Firm Templates';
    default:
      return 'Templates';
  }
};

const AddDocumentEmptyState = ({
  title,
  description,
}: {
  title: string;
  description: string;
}) => (
  <div
    className={cn(
      DOCUMENT_CARD_BASE,
      'flex h-full min-h-[420px] items-center justify-center px-8 py-10 text-center',
    )}
  >
    <div className="max-w-sm space-y-2">
      <p className="text-base font-semibold text-zinc-900">{title}</p>
      <p className="text-sm leading-6 text-zinc-500">{description}</p>
    </div>
  </div>
);

const PdfImportModeSelector = ({
  value,
  onChange,
}: {
  value: PdfImportMode;
  onChange: (value: PdfImportMode) => void;
}) => (
  <div className="mt-4">
    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
      PDF Handling
    </p>
    <div className="mt-2 flex flex-col gap-2">
      <button
        type="button"
        onClick={() => onChange('as_pdf')}
        className={cn(
          'flex w-full items-center justify-center rounded-[12px] border px-3 py-2.5 text-[13px] font-medium transition-colors',
          value === 'as_pdf'
            ? 'border-zinc-300 bg-white text-zinc-900 shadow-[0_1px_2px_rgba(15,23,42,0.06)]'
            : 'border-zinc-200 bg-zinc-50/70 text-zinc-600 hover:border-zinc-300 hover:bg-white hover:text-zinc-800',
        )}
      >
        Add as PDF
      </button>
      <button
        type="button"
        onClick={() => onChange('convert_to_docx')}
        className={cn(
          'flex w-full items-center justify-center rounded-[12px] border px-3 py-2.5 text-[13px] font-medium transition-colors',
          value === 'convert_to_docx'
            ? 'border-indigo-300 bg-indigo-50 text-indigo-700 shadow-[0_1px_2px_rgba(79,70,229,0.12)]'
            : 'border-zinc-200 bg-zinc-50/70 text-zinc-600 hover:border-zinc-300 hover:bg-white hover:text-zinc-800',
        )}
      >
        Convert to DOCX
      </button>
    </div>
  </div>
);

const LoadingPreview = ({ label }: { label: string }) => (
  <div className="flex h-full min-h-[420px] items-center justify-center">
    <div className="flex flex-col items-center gap-2">
      <Loader2Icon className="h-6 w-6 animate-spin text-zinc-500" />
      <span className="text-sm font-medium text-zinc-500">{label}</span>
    </div>
  </div>
);

const CompactPreviewLoading = ({ label }: { label: string }) => (
  <div className="flex h-full items-center justify-center">
    <div className="flex flex-col items-center gap-2">
      <Loader2Icon className="h-5 w-5 animate-spin text-zinc-500" />
      <span className="text-sm font-medium text-zinc-500">{label}</span>
    </div>
  </div>
);

const CompactPreviewError = ({ message }: { message: string }) => (
  <div className="flex h-full items-center justify-center p-6 text-center">
    <p className="text-sm font-medium text-zinc-500">{message}</p>
  </div>
);

export const DocumentPreviewModal = ({
  isOpen,
  onClose,
  initialComponentId,
  onPotentialOnlyOfficeSave,
  initialAddDocumentKind,
}: DocumentPreviewModalProps) => {
  const [viewportHeight, setViewportHeight] = useState(() =>
    typeof window === 'undefined' ? 900 : window.innerHeight,
  );

  useEffect(() => {
    const handleResize = () => setViewportHeight(window.innerHeight);

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const {
    matterId,
    providerId,
    requestId,
    packageData,
    canEditPackage,
    primaryClient,
    refetchPackage,
  } = useRequestPage();

  const isCompactHeight = viewportHeight <= SHORT_VIEWPORT_HEIGHT;
  const previewHeightClass = isCompactHeight
    ? 'h-[220px] xl:h-[280px]'
    : COMPACT_PREVIEW_HEIGHT;
  const sidebarWidthClass = isCompactHeight
    ? 'w-[200px] xl:w-[220px]'
    : COMPACT_SIDEBAR_WIDTH;
  const documentPickerSidebarWidthClass = isCompactHeight
    ? 'w-[200px] xl:w-[220px]'
    : 'w-[220px] xl:w-[240px] 2xl:w-[252px]';
  const visiblePackageComponents = useMemo(
    () => getVisiblePackageComponents(packageData?.components),
    [packageData?.components],
  );
  const packageComponentsById = useMemo(
    () =>
      new Map((packageData?.components ?? []).map((component) => [component.id, component])),
    [packageData?.components],
  );

  const [createdComponentsById, setCreatedComponentsById] = useState<
    Record<string, PackageComponentSchema>
  >({});
  const visibleComponents = useMemo(() => {
    const componentIds = new Set(
      visiblePackageComponents.map((component) => component.id),
    );
    Object.keys(createdComponentsById).forEach((componentId) => {
      componentIds.add(componentId);
    });

    return Array.from(componentIds)
      .map(
        (componentId) =>
          packageComponentsById.get(componentId) ??
          createdComponentsById[componentId],
      )
      .filter(Boolean)
      .sort(
        (left, right) => (left.order ?? Number.MAX_SAFE_INTEGER) - (right.order ?? Number.MAX_SAFE_INTEGER),
      ) as PackageComponentSchema[];
  }, [createdComponentsById, packageComponentsById, visiblePackageComponents]);
  let previousPackageBlobId: string | null = null;
  if (typeof packageData?.completed_package_id === 'string') {
    previousPackageBlobId = packageData.completed_package_id;
  } else if (packageData?.completed_package_id) {
    previousPackageBlobId = String(packageData.completed_package_id);
  }
  const previousPackagePreviewUrl =
    packageData?.download_url || packageData?.rendered_pdf_url || null;
  const hasPreviousPackageArtifact = Boolean(
    previousPackageBlobId || previousPackagePreviewUrl,
  );
  const isPreviousPackageOnlyState =
    hasPreviousPackageArtifact && visibleComponents.length === 0;
  const canManageDocuments = canEditPackage || hasPreviousPackageArtifact;

  const [activeTab, setActiveTab] = useState<ModalTabState>({ type: 'add' });

  const selectedComponent =
    activeTab.type === 'component'
      ? visibleComponents.find(
          (component) => component.id === activeTab.componentId,
        ) ?? null
      : null;

  const [editorConfig, setEditorConfig] = useState<EditorConfig | null>(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [fetchEditorConfig] = useLazyGetHipaaPackageComponentEditorConfigQuery();
  const [fetchBlobEditorConfig] = useLazyGetBlobEditorConfigQuery();
  const [fetchTemplateEditorConfig] = useLazyGetTemplateEditorConfigQuery();
  const [convertBlobToDocx, { isLoading: isConvertingBlobToDocx }] =
    useConvertBlobToDocxMutation();
  const [addComponent, { isLoading: isAddingComponent }] =
    useAddComponentMutation();
  const [removeComponent, { isLoading: isRemovingComponent }] =
    useRemoveComponentMutation();
  const lastLoadedComponentIdRef = useRef<string | null>(null);

  const {
    data: matterDetails,
    isLoading: isLoadingMatterDetails,
  } = useGetMatterDetailsQuery(matterId, {
    skip: !isOpen || !matterId,
  });
  const firmId = matterDetails?.firm?.id;
  const { data: firmDetails } = useGetFirmQuery(firmId ?? '', {
    skip: !isOpen || !firmId,
  });
  const brandingMode =
    (
      matterDetails?.firm as
        | { medrecs_branding_mode?: MedRecsBrandingMode | null }
        | undefined
    )?.medrecs_branding_mode ?? MedRecsBrandingMode.CUSTOM;
  const showLighthouseLibrary = brandingMode !== MedRecsBrandingMode.CUSTOM;

  const {
    data: matterBlobs = [],
    isLoading: isLoadingMatterBlobs,
    refetch: refetchMatterBlobs,
  } =
    useGetBlobsQuery(matterId, {
      skip: !isOpen || !matterId,
    });
  const { data: previousPackageBlobDetail } = useGetBlobDetailQuery(
    {
      matterId,
      blobId: previousPackageBlobId ?? '',
    },
    {
      skip: !isOpen || !matterId || !previousPackageBlobId,
    },
  );
  const { data: collectionTree } = useGetCollectionTreeQuery(matterId, {
    skip: !isOpen || !matterId,
  });
  const { handleUploadToCollectionAndReturn } = useUploadManagerUnified({
    matterId,
    refetch: refetchMatterBlobs,
  });

  const { data: firmTemplatesData, isLoading: isLoadingFirmTemplates } =
    useTemplateListQuery(
      { firmId: firmId || '', scope: 'FIRM', status: 'ACTIVE', limit: 500 },
      { skip: !isOpen || !firmId },
    );

  const { data: globalTemplatesData, isLoading: isLoadingGlobalTemplates } =
    adminTemplatesApi.useAdminTemplatesListQuery(
      {
        isGlobal: true,
        includeDeleted: false,
        status: 'ACTIVE',
        limit: 500,
      },
      { skip: !isOpen },
    );

  const templateOptions = useMemo(() => {
    const firmTemplates =
      firmTemplatesData?.results
        ?.map((template) =>
          normalizeTemplate(
            { ...template, firm_id: template.firm_id ?? firmId ?? null },
            showLighthouseLibrary,
          ),
        )
        .filter(Boolean) ?? [];

    const globalTemplates =
      globalTemplatesData?.results
        ?.map((template) =>
          normalizeTemplate(
            { ...template, firm_id: null },
            showLighthouseLibrary,
          ),
        )
        .filter(Boolean) ?? [];

    return sortTemplates([...firmTemplates, ...globalTemplates] as TemplateOption[]);
  }, [
    firmId,
    firmTemplatesData?.results,
    globalTemplatesData?.results,
    showLighthouseLibrary,
  ]);

  const existingPackageBlobIds = useMemo(
    () =>
      new Set(
        (packageData?.components ?? [])
          .map((component) => component.blob_id)
          .filter(Boolean),
      ),
    [packageData?.components],
  );

  const matterDocumentOptions = useMemo(
    () =>
      matterBlobs
        .filter(isDocumentBlob)
        .filter((blob) => !existingPackageBlobIds.has(blob.id))
        .sort((left, right) =>
          (left.name || left.original_filename || '').localeCompare(
            right.name || right.original_filename || '',
          ),
        ),
    [existingPackageBlobIds, matterBlobs],
  );

  const signedPdfOptions = useMemo(
    () =>
      matterBlobs
        .filter(isPdfBlob)
        .sort((left, right) =>
          (right.modified_at || '').localeCompare(left.modified_at || ''),
        ),
    [matterBlobs],
  );

  const staticDocumentPreviewUrls: Record<StaticDocumentTag, string | null> =
    useMemo(
      () => ({
        attorney_relationship:
          firmDetails?.hipaa_attorney_relationship_form_url ?? null,
        reproductive_rights:
          firmDetails?.hipaa_reproductive_rights_attestation_url ?? null,
        client_id: null,
        texas_affidavit: buildBackendUrl('/static/texas_affidavit.pdf'),
      }),
      [
        firmDetails?.hipaa_attorney_relationship_form_url,
        firmDetails?.hipaa_reproductive_rights_attestation_url,
      ],
    );

  const staticDocumentOptions = useMemo<StaticDocumentOption[]>(
    () => [
      {
        tag: 'attorney_relationship',
        description: 'Document establishing attorney-client relationship.',
        previewUrl: staticDocumentPreviewUrls.attorney_relationship,
      },
      {
        tag: 'reproductive_rights',
        description: 'Attestation regarding reproductive health information.',
        previewUrl: staticDocumentPreviewUrls.reproductive_rights,
      },
      {
        tag: 'client_id',
        description: 'Standard client identification requirement form.',
        previewUrl: staticDocumentPreviewUrls.client_id,
      },
      {
        tag: 'texas_affidavit',
        description:
          'Blank affidavit form for Texas medical records requests.',
        previewUrl: staticDocumentPreviewUrls.texas_affidavit,
      },
    ],
    [staticDocumentPreviewUrls],
  );

  const [addDocumentKind, setAddDocumentKind] =
    useState<AddDocumentKind>('coversheet');
  const [authorizationMode, setAuthorizationMode] =
    useState<AuthorizationMode>('standard_docx');
  const [selectedLibrary, setSelectedLibrary] =
    useState<TemplateLibraryKey>('global');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null,
  );
  const [selectedMergeTemplateId, setSelectedMergeTemplateId] = useState<
    string | null
  >(null);
  const [selectedMatterBlobId, setSelectedMatterBlobId] = useState<
    string | null
  >(null);
  const [selectedStaticDocumentTag, setSelectedStaticDocumentTag] =
    useState<StaticDocumentTag | null>(null);
  const [extractionBlob, setExtractionBlob] = useState<MatterBlob | null>(null);
  const [selectedSignedBlobId, setSelectedSignedBlobId] = useState<
    string | null
  >(null);
  const [isSignatureCaptureOpen, setIsSignatureCaptureOpen] = useState(false);
  const [isMatterDocumentPickerOpen, setIsMatterDocumentPickerOpen] =
    useState(false);
  const [matterDocumentPickerSelectionIds, setMatterDocumentPickerSelectionIds] =
    useState<string[]>([]);
  const [isSignedPdfPickerOpen, setIsSignedPdfPickerOpen] = useState(false);
  const [signedPdfPickerSelectionIds, setSignedPdfPickerSelectionIds] =
    useState<string[]>([]);
  const [matterPdfImportMode, setMatterPdfImportMode] =
    useState<PdfImportMode>('as_pdf');
  const [staticPreviewObjectUrl, setStaticPreviewObjectUrl] = useState<
    string | null
  >(null);
  const [isLoadingStaticPreview, setIsLoadingStaticPreview] = useState(false);
  const [staticPreviewError, setStaticPreviewError] = useState<string | null>(
    null,
  );
  const [selectedUploadFile, setSelectedUploadFile] = useState<File | null>(
    null,
  );
  const [uploadPdfImportMode, setUploadPdfImportMode] =
    useState<PdfImportMode>('as_pdf');
  const [isUploadingManualDocument, setIsUploadingManualDocument] =
    useState(false);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const hasSeenEditableOnlyOfficeSessionRef = useRef(false);
  const hasAppliedInitialAddDocumentKindRef = useRef(false);
  const editedComponentSnapshotsRef = useRef<Map<string, string | null>>(
    new Map(),
  );

  const activeDocxCategoryFilters = useMemo<
    Array<'HIPAA_COVERSHEET' | 'HIPAA_AUTHORIZATION' | 'FIRM_AUTHORIZATION'>
  >(() => {
    if (addDocumentKind === 'coversheet') {
      return ['HIPAA_COVERSHEET'];
    }

    if (
      addDocumentKind === 'authorization' &&
      authorizationMode === 'standard_docx'
    ) {
      return ['HIPAA_AUTHORIZATION', 'FIRM_AUTHORIZATION'];
    }

    return [];
  }, [addDocumentKind, authorizationMode]);

  const filteredDocxTemplates = useMemo(
    () =>
      templateOptions.filter(
        (template) =>
          template.format === 'DOCX' &&
          activeDocxCategoryFilters.includes(template.category),
      ),
    [activeDocxCategoryFilters, templateOptions],
  );

  const availableLibraries = useMemo(
    () =>
      Array.from(
        new Set(filteredDocxTemplates.map((template) => template.library)),
      ) as TemplateLibraryKey[],
    [filteredDocxTemplates],
  );

  const libraryTemplates = useMemo(
    () =>
      filteredDocxTemplates.filter(
        (template) => template.library === selectedLibrary,
      ),
    [filteredDocxTemplates, selectedLibrary],
  );

  const selectedTemplate = useMemo(
    () =>
      libraryTemplates.find((template) => template.id === selectedTemplateId) ??
      null,
    [libraryTemplates, selectedTemplateId],
  );

  const mergeTemplateOptions = useMemo(
    () =>
      templateOptions.filter(
        (template) =>
          (template.category === 'HIPAA_AUTHORIZATION' ||
            template.category === 'FIRM_AUTHORIZATION') &&
          template.format === 'PDF',
      ),
    [templateOptions],
  );

  const mergeAvailableLibraries = useMemo(
    () =>
      Array.from(
        new Set(mergeTemplateOptions.map((template) => template.library)),
      ) as TemplateLibraryKey[],
    [mergeTemplateOptions],
  );

  const mergeLibraryTemplates = useMemo(
    () =>
      mergeTemplateOptions.filter(
        (template) => template.library === selectedLibrary,
      ),
    [mergeTemplateOptions, selectedLibrary],
  );

  const selectedMergeTemplate = useMemo(() => {
    return (
      mergeLibraryTemplates.find(
        (template) => template.id === selectedMergeTemplateId,
      ) ?? null
    );
  }, [mergeLibraryTemplates, selectedMergeTemplateId]);

  const selectedMatterBlob = useMemo(
    () =>
      matterDocumentOptions.find((blob) => blob.id === selectedMatterBlobId) ??
      null,
    [matterDocumentOptions, selectedMatterBlobId],
  );
  const selectedStaticDocument = useMemo(
    () =>
      staticDocumentOptions.find(
        (document) => document.tag === selectedStaticDocumentTag,
      ) ?? null,
    [selectedStaticDocumentTag, staticDocumentOptions],
  );
  const selectedMatterBlobIsWordDocument = useMemo(
    () => (selectedMatterBlob ? isWordBlob(selectedMatterBlob) : false),
    [selectedMatterBlob],
  );
  const selectedMatterBlobIsPdfDocument = useMemo(
    () => (selectedMatterBlob ? isPdfBlob(selectedMatterBlob) : false),
    [selectedMatterBlob],
  );
  const selectedMatterBlobSupportsSignatureCapture = useMemo(
    () =>
      Boolean(
        selectedMatterBlob &&
          primaryClient?.id &&
          isMatterSignatureSourceBlob(selectedMatterBlob),
      ),
    [primaryClient?.id, selectedMatterBlob],
  );
  const { data: selectedMatterBlobDetail } = useGetBlobDetailQuery(
    {
      matterId,
      blobId: selectedMatterBlob?.id ?? '',
    },
    {
      skip: !isOpen || !matterId || !selectedMatterBlob?.id,
    },
  );
  const selectedMatterBlobPreviewUrl = useMemo(() => {
    if (!selectedMatterBlobDetail) return null;

    if (selectedMatterBlobIsPdfDocument) {
      return (
        selectedMatterBlobDetail.pdf_preview_url ||
        selectedMatterBlobDetail.download_url
      );
    }

    return selectedMatterBlobDetail.download_url;
  }, [selectedMatterBlobDetail, selectedMatterBlobIsPdfDocument]);

  const handleMatterBlobExtractionComplete = useCallback(
    async (newBlobId: string) => {
      setExtractionBlob(null);
      await refetchMatterBlobs();
      setSelectedMatterBlobId(newBlobId);
    },
    [refetchMatterBlobs],
  );

  const selectedSignedBlob = useMemo(
    () =>
      signedPdfOptions.find((blob) => blob.id === selectedSignedBlobId) ?? null,
    [selectedSignedBlobId, signedPdfOptions],
  );
  const uploadTargetCollectionId = collectionTree?.id ?? null;
  const selectedUploadIsPdf = useMemo(
    () => (selectedUploadFile ? isPdfFile(selectedUploadFile) : false),
    [selectedUploadFile],
  );
  const selectedUploadIsWord = useMemo(
    () => (selectedUploadFile ? isWordFile(selectedUploadFile) : false),
    [selectedUploadFile],
  );
  const selectedUploadPreviewUrl = useMemo(() => {
    if (!selectedUploadFile || !selectedUploadIsPdf) return null;
    return URL.createObjectURL(selectedUploadFile);
  }, [selectedUploadFile, selectedUploadIsPdf]);
  const selectedUploadBadgeLabel = useMemo(() => {
    if (!selectedUploadFile) {
      return 'Upload';
    }

    if (selectedUploadIsPdf && uploadPdfImportMode === 'convert_to_docx') {
      return 'DOCX';
    }

    if (selectedUploadIsPdf) {
      return 'PDF';
    }

    if (selectedUploadIsWord) {
      return 'DOCX';
    }

    return 'Upload';
  }, [
    selectedUploadFile,
    selectedUploadIsPdf,
    selectedUploadIsWord,
    uploadPdfImportMode,
  ]);

  useEffect(() => {
    return () => {
      if (selectedUploadPreviewUrl) {
        URL.revokeObjectURL(selectedUploadPreviewUrl);
      }
    };
  }, [selectedUploadPreviewUrl]);

  const [templatePreviewEditorConfig, setTemplatePreviewEditorConfig] =
    useState<EditorConfig | null>(null);
  const [templatePreviewError, setTemplatePreviewError] = useState<
    string | null
  >(null);
  const [isLoadingTemplatePreview, setIsLoadingTemplatePreview] =
    useState(false);

  const [matterBlobPreviewEditorConfig, setMatterBlobPreviewEditorConfig] =
    useState<EditorConfig | null>(null);
  const [matterBlobPreviewError, setMatterBlobPreviewError] = useState<
    string | null
  >(null);
  const [isLoadingMatterBlobPreview, setIsLoadingMatterBlobPreview] =
    useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setActiveTab((currentTab) => {
      if (
        currentTab.type === 'previous-package' &&
        hasPreviousPackageArtifact &&
        visibleComponents.length === 0
      ) {
        return currentTab;
      }

      if (
        currentTab.type === 'component' &&
        visibleComponents.some(
          (component) => component.id === currentTab.componentId,
        )
      ) {
        return currentTab;
      }

      if (
        initialComponentId &&
        visibleComponents.some((component) => component.id === initialComponentId)
      ) {
        return { type: 'component', componentId: initialComponentId };
      }

      if (visibleComponents[0]) {
        return { type: 'component', componentId: visibleComponents[0].id };
      }

      if (hasPreviousPackageArtifact) {
        return { type: 'previous-package' };
      }

      return { type: 'add' };
    });
  }, [
    hasPreviousPackageArtifact,
    initialAddDocumentKind,
    initialComponentId,
    isOpen,
    visibleComponents,
  ]);

  useEffect(() => {
    if (!isOpen) {
      hasAppliedInitialAddDocumentKindRef.current = false;
      return;
    }

    if (
      !initialAddDocumentKind ||
      hasAppliedInitialAddDocumentKindRef.current
    ) {
      return;
    }

    hasAppliedInitialAddDocumentKindRef.current = true;
    setAddDocumentKind(initialAddDocumentKind);
    setActiveTab({ type: 'add' });
  }, [initialAddDocumentKind, isOpen]);

  useEffect(() => {
    if (
      addDocumentKind === 'matter_document' ||
      addDocumentKind === 'static_document' ||
      (addDocumentKind === 'authorization' && authorizationMode === 'pdf_merge')
    ) {
      return;
    }

    const nextLibrary =
      availableLibraries.includes(selectedLibrary) && selectedLibrary
        ? selectedLibrary
        : pickPreferredLibrary(availableLibraries);

    if (!nextLibrary) {
      setSelectedTemplateId(null);
      return;
    }

    if (nextLibrary !== selectedLibrary) {
      setSelectedLibrary(nextLibrary);
      return;
    }

    const nextTemplate =
      libraryTemplates.find((template) => template.id === selectedTemplateId) ??
      pickPreferredTemplate(libraryTemplates);

    setSelectedTemplateId(nextTemplate?.id ?? null);
  }, [
    addDocumentKind,
    authorizationMode,
    availableLibraries,
    libraryTemplates,
    selectedLibrary,
    selectedTemplateId,
  ]);

  useEffect(() => {
    if (
      addDocumentKind !== 'authorization' ||
      authorizationMode !== 'pdf_merge'
    ) {
      return;
    }

    const nextLibrary =
      mergeAvailableLibraries.includes(selectedLibrary) && selectedLibrary
        ? selectedLibrary
        : pickPreferredLibrary(mergeAvailableLibraries);

    if (!nextLibrary) {
      setSelectedMergeTemplateId(null);
      return;
    }

    if (nextLibrary !== selectedLibrary) {
      setSelectedLibrary(nextLibrary);
      return;
    }

    const nextTemplate =
      mergeLibraryTemplates.find(
        (template) => template.id === selectedMergeTemplateId,
      ) ?? pickPreferredTemplate(mergeLibraryTemplates);

    setSelectedMergeTemplateId(nextTemplate?.id ?? null);
  }, [
    addDocumentKind,
    authorizationMode,
    mergeAvailableLibraries,
    mergeLibraryTemplates,
    selectedLibrary,
    selectedMergeTemplateId,
  ]);

  useEffect(() => {
    if (addDocumentKind !== 'matter_document') return;

    if (
      selectedMatterBlobId &&
      matterDocumentOptions.some((blob) => blob.id === selectedMatterBlobId)
    ) {
      return;
    }

    setSelectedMatterBlobId(matterDocumentOptions[0]?.id ?? null);
  }, [addDocumentKind, matterDocumentOptions, selectedMatterBlobId]);

  useEffect(() => {
    if (addDocumentKind !== 'static_document') return;

    if (
      selectedStaticDocumentTag &&
      staticDocumentOptions.some(
        (document) => document.tag === selectedStaticDocumentTag,
      )
    ) {
      return;
    }

    setSelectedStaticDocumentTag(staticDocumentOptions[0]?.tag ?? null);
  }, [addDocumentKind, selectedStaticDocumentTag, staticDocumentOptions]);

  useEffect(() => {
    if (!selectedMatterBlobIsPdfDocument) {
      setMatterPdfImportMode('as_pdf');
    }
  }, [selectedMatterBlobIsPdfDocument]);

  useEffect(() => {
    if (!selectedUploadIsPdf) {
      setUploadPdfImportMode('as_pdf');
    }
  }, [selectedUploadIsPdf]);

  useEffect(() => {
    let isActive = true;
    let nextObjectUrl: string | null = null;

    const loadStaticPreview = async () => {
      if (!selectedStaticDocument?.previewUrl) {
        setStaticPreviewError(null);
        setIsLoadingStaticPreview(false);
        setStaticPreviewObjectUrl(null);
        return;
      }

      setIsLoadingStaticPreview(true);
      setStaticPreviewError(null);

      try {
        const response = await fetch(selectedStaticDocument.previewUrl, {
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`Preview request failed (${response.status})`);
        }

        const blob = await response.blob();
        nextObjectUrl = URL.createObjectURL(blob);

        if (!isActive) {
          URL.revokeObjectURL(nextObjectUrl);
          return;
        }

        setStaticPreviewObjectUrl(nextObjectUrl);
      } catch (error) {
        if (!isActive) return;

        setStaticPreviewObjectUrl(null);
        setStaticPreviewError(
          error instanceof Error
            ? error.message
            : 'Preview unavailable for this static document.',
        );
      } finally {
        if (isActive) {
          setIsLoadingStaticPreview(false);
        }
      }
    };

    setStaticPreviewObjectUrl((currentUrl) => {
      if (currentUrl) {
        URL.revokeObjectURL(currentUrl);
      }
      return null;
    });

    loadStaticPreview();

    return () => {
      isActive = false;
      if (nextObjectUrl) {
        URL.revokeObjectURL(nextObjectUrl);
      }
    };
  }, [selectedStaticDocument?.previewUrl]);

  useEffect(() => {
    if (
      addDocumentKind !== 'authorization' ||
      authorizationMode !== 'pdf_merge'
    ) {
      return;
    }

    const cachedSelection = getSignedAuthorizationSelection(matterId);
    const preferredBlob =
      signedPdfOptions.find((blob) => blob.id === cachedSelection?.signedBlobId) ??
      signedPdfOptions.find((blob) => {
        const filename = (blob.original_filename || blob.name || '').toLowerCase();
        return filename.includes('hipaa') || filename.includes('authorization');
      }) ??
      signedPdfOptions[0];

    if (
      selectedSignedBlobId &&
      signedPdfOptions.some((blob) => blob.id === selectedSignedBlobId)
    ) {
      return;
    }

    setSelectedSignedBlobId(preferredBlob?.id ?? null);
  }, [
    addDocumentKind,
    authorizationMode,
    matterId,
    selectedSignedBlobId,
    signedPdfOptions,
  ]);

  useEffect(() => {
    if (
      !isOpen ||
      !firmId ||
      !selectedTemplate ||
      selectedTemplate.format !== 'DOCX'
    ) {
      setTemplatePreviewEditorConfig(null);
      setTemplatePreviewError(null);
      setIsLoadingTemplatePreview(false);
      return undefined;
    }

    let isMounted = true;
    setTemplatePreviewEditorConfig(null);
    setTemplatePreviewError(null);
    setIsLoadingTemplatePreview(true);

    fetchTemplateEditorConfig({
      firmId,
      templateId: selectedTemplate.id,
      mode: 'view',
    })
      .unwrap()
      .then((config) => {
        if (!isMounted) return;
        setTemplatePreviewEditorConfig(config);
      })
      .catch(() => {
        if (!isMounted) return;
        setTemplatePreviewError('Unable to load a DOCX preview.');
      })
      .finally(() => {
        if (!isMounted) return;
        setIsLoadingTemplatePreview(false);
      });

    return () => {
      isMounted = false;
    };
  }, [fetchTemplateEditorConfig, firmId, isOpen, selectedTemplate]);

  useEffect(() => {
    if (
      !isOpen ||
      !selectedMatterBlob ||
      !selectedMatterBlobIsWordDocument
    ) {
      setMatterBlobPreviewEditorConfig(null);
      setMatterBlobPreviewError(null);
      setIsLoadingMatterBlobPreview(false);
      return undefined;
    }

    let isMounted = true;
    setMatterBlobPreviewEditorConfig(null);
    setMatterBlobPreviewError(null);
    setIsLoadingMatterBlobPreview(true);

    fetchBlobEditorConfig({
      matterId,
      blobId: selectedMatterBlob.id,
      mode: 'view',
    })
      .unwrap()
      .then((config) => {
        if (!isMounted) return;
        setMatterBlobPreviewEditorConfig(config);
      })
      .catch(() => {
        if (!isMounted) return;
        setMatterBlobPreviewError('Unable to load a DOCX preview.');
      })
      .finally(() => {
        if (!isMounted) return;
        setIsLoadingMatterBlobPreview(false);
      });

    return () => {
      isMounted = false;
    };
  }, [
    fetchBlobEditorConfig,
    isOpen,
    matterId,
    selectedMatterBlob,
    selectedMatterBlobIsWordDocument,
  ]);

  const loadEditorConfig = useCallback(
    async (component: PackageComponentSchema) => {
      if (!packageData?.id) return;
      if (!isOnlyOfficeEditablePackageComponent(component)) return;
      if (lastLoadedComponentIdRef.current === component.id) return;

      setEditorConfig(null);
      setIsLoadingConfig(true);

      try {
        const config = await fetchEditorConfig({
          matterId,
          providerId,
          requestId,
          packageId: packageData.id,
          componentId: component.id,
        }).unwrap();

        setEditorConfig(config);
        lastLoadedComponentIdRef.current = component.id;
      } catch {
        toast.error('Failed to load document editor');
      } finally {
        setIsLoadingConfig(false);
      }
    },
    [fetchEditorConfig, matterId, packageData?.id, providerId, requestId],
  );

  useEffect(() => {
    if (!isOpen || !selectedComponent) return;

    if (!isOnlyOfficeEditablePackageComponent(selectedComponent)) {
      setEditorConfig(null);
      setIsLoadingConfig(false);
      lastLoadedComponentIdRef.current = null;
      return;
    }

    const run = async () => {
      await loadEditorConfig(selectedComponent);
    };

    run();
  }, [isOpen, loadEditorConfig, selectedComponent]);

  useEffect(() => {
    if (!isOpen || activeTab.type !== 'component' || !selectedComponent || !editorConfig) {
      return;
    }

    if (
      isOnlyOfficeEditablePackageComponent(selectedComponent) &&
      editorConfig.config.editorConfig.mode !== 'view'
    ) {
      hasSeenEditableOnlyOfficeSessionRef.current = true;
    }
  }, [activeTab.type, editorConfig, isOpen, selectedComponent]);

  const handleOnlyOfficeDocumentStateChange = useCallback(
    (hasChanges: boolean) => {
      if (!hasChanges || !selectedComponent) {
        return;
      }

      editedComponentSnapshotsRef.current.set(
        selectedComponent.id,
        (selectedComponent as PackageComponentWithModifiedAt).modified_at ||
          null,
      );
    },
    [selectedComponent],
  );

  const handleClose = useCallback(() => {
    if (editedComponentSnapshotsRef.current.size > 0) {
      onPotentialOnlyOfficeSave?.(
        Array.from(editedComponentSnapshotsRef.current.entries()).map(
          ([componentId, modifiedAt]) => ({
            componentId,
            modifiedAt,
            waitMode: 'strict' as const,
          }),
        ),
      );
    } else if (
      hasSeenEditableOnlyOfficeSessionRef.current &&
      selectedComponent &&
      isOnlyOfficeEditablePackageComponent(selectedComponent)
    ) {
      onPotentialOnlyOfficeSave?.([
        {
          componentId: selectedComponent.id,
          modifiedAt:
            (selectedComponent as PackageComponentWithModifiedAt).modified_at ||
            null,
          waitMode: 'grace',
        },
      ]);
    }
    setEditorConfig(null);
    setCreatedComponentsById({});
    setSelectedTemplateId(null);
    setSelectedMatterBlobId(null);
    setSelectedStaticDocumentTag(null);
    setSelectedSignedBlobId(null);
    setIsSignatureCaptureOpen(false);
    setIsMatterDocumentPickerOpen(false);
    setMatterDocumentPickerSelectionIds([]);
    setIsSignedPdfPickerOpen(false);
    setSignedPdfPickerSelectionIds([]);
    setSelectedMergeTemplateId(null);
    setSelectedUploadFile(null);
    setAddDocumentKind('coversheet');
    setAuthorizationMode('standard_docx');
    setSelectedLibrary('global');
    setActiveTab({ type: 'add' });
    lastLoadedComponentIdRef.current = null;
    hasAppliedInitialAddDocumentKindRef.current = false;
    hasSeenEditableOnlyOfficeSessionRef.current = false;
    editedComponentSnapshotsRef.current.clear();
    onClose();
  }, [onClose, onPotentialOnlyOfficeSave, selectedComponent]);

  const trackCreatedComponent = useCallback(
    async (component: PackageComponentSchema, successMessage: string) => {
      setCreatedComponentsById((current) => ({
        ...current,
        [component.id]: component,
      }));

      await refetchPackage();

      setActiveTab({ type: 'component', componentId: component.id });
      toast.success(successMessage);
    },
    [refetchPackage],
  );

  const handleCreateComponent = useCallback(
    async (payload: CreatePackageComponent, successMessage: string) => {
      if (!packageData?.id) return;

      const component = await addComponent({
        matterId,
        providerId,
        requestId,
        packageId: packageData.id,
        createPackageComponent: payload,
      }).unwrap();

      await trackCreatedComponent(component, successMessage);
    },
    [
      addComponent,
      matterId,
      packageData?.id,
      providerId,
      requestId,
      trackCreatedComponent,
    ],
  );

  const handleAddTemplate = useCallback(async () => {
    if (!selectedTemplate) return;

    await handleCreateComponent(
      {
        component_type: 'template',
        v2_template_id: selectedTemplate.id,
      },
      `${selectedTemplate.name} added to the package`,
    );
  }, [handleCreateComponent, selectedTemplate]);

  const handleAddMatterDocument = useCallback(async (
    importMode: PdfImportMode = matterPdfImportMode,
  ) => {
    if (!selectedMatterBlob) return;

    if (selectedMatterBlobIsPdfDocument && importMode === 'convert_to_docx') {
      const convertedBlob = await convertBlobToDocx({
        matterId,
        blobId: selectedMatterBlob.id,
      }).unwrap();

      await refetchMatterBlobs();

      await handleCreateComponent(
        {
          component_type: 'matter_blob',
          blob_id: convertedBlob.id,
        },
        `${selectedMatterBlob.name || selectedMatterBlob.original_filename || 'Matter document'} converted to DOCX and added to the package`,
      );
      return;
    }

    await handleCreateComponent(
      {
        component_type: 'matter_blob',
        blob_id: selectedMatterBlob.id,
      },
      `${selectedMatterBlob.name || selectedMatterBlob.original_filename || 'Matter document'} added to the package`,
    );
  }, [
    convertBlobToDocx,
    handleCreateComponent,
    matterId,
    matterPdfImportMode,
    refetchMatterBlobs,
    selectedMatterBlob,
    selectedMatterBlobIsPdfDocument,
  ]);

  const handleAddStaticDocument = useCallback(async () => {
    if (!selectedStaticDocumentTag) return;

    await handleCreateComponent(
      {
        component_type: 'static_document',
        static_document_tag: selectedStaticDocumentTag,
      },
      `${StaticDocumentLabels[selectedStaticDocumentTag]} added to the package`,
    );
  }, [handleCreateComponent, selectedStaticDocumentTag]);

  const handleAddPdfMerge = useCallback(async () => {
    if (!selectedSignedBlob || !selectedMergeTemplate) return;

    await handleCreateComponent(
      {
        component_type: 'signed_authorization_merge',
        v2_template_id: selectedMergeTemplate.id,
        signed_blob_id: selectedSignedBlob.id,
      },
      'Signed authorization merge added to the package',
    );

    setSignedAuthorizationSelection(matterId, {
      signedBlobId: selectedSignedBlob.id,
      templateId: selectedMergeTemplate.id,
    });
  }, [
    handleCreateComponent,
    matterId,
    selectedMergeTemplate,
    selectedSignedBlob,
  ]);

  const handleRemoveComponent = useCallback(
    async (componentId: string) => {
      if (!packageData?.id) return;

      const remainingComponents = visibleComponents.filter(
        (component) => component.id !== componentId,
      );

      await removeComponent({
        matterId,
        providerId,
        requestId,
        packageId: packageData.id,
        id: componentId,
      }).unwrap();

      toast.success('Document removed from the package');

      setActiveTab((currentTab) => {
        if (currentTab.type !== 'component' || currentTab.componentId !== componentId) {
          return currentTab;
        }

        if (remainingComponents[0]) {
          return { type: 'component', componentId: remainingComponents[0].id };
        }

        if (hasPreviousPackageArtifact) {
          return { type: 'previous-package' };
        }

        return { type: 'add' };
      });
    },
    [
      hasPreviousPackageArtifact,
      matterId,
      packageData?.id,
      providerId,
      removeComponent,
      requestId,
      visibleComponents,
    ],
  );

  const handlePickUploadFile = useCallback(() => {
    uploadInputRef.current?.click();
  }, []);

  const handleUploadFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextFile = event.target.files?.[0] ?? null;
      setSelectedUploadFile(nextFile);
      event.target.value = '';
    },
    [],
  );

  const handleAddUploadedDocument = useCallback(async () => {
    if (!selectedUploadFile || !uploadTargetCollectionId) {
      toast.error('Select a file to upload first');
      return;
    }

    setIsUploadingManualDocument(true);

    try {
      const [uploadedBlob] = await handleUploadToCollectionAndReturn(
        [selectedUploadFile],
        uploadTargetCollectionId,
      );

      if (!uploadedBlob) {
        throw new Error('Upload failed');
      }

      let blobIdToAdd = uploadedBlob.id;
      let successMessage = `${selectedUploadFile.name} added to the package`;

      if (selectedUploadIsPdf && uploadPdfImportMode === 'convert_to_docx') {
        const convertedBlob = await convertBlobToDocx({
          matterId,
          blobId: uploadedBlob.id,
        }).unwrap();
        blobIdToAdd = convertedBlob.id;
        successMessage = `${selectedUploadFile.name} converted to DOCX and added to the package`;
      }

      await refetchMatterBlobs();

      await handleCreateComponent(
        {
          component_type: 'matter_blob',
          blob_id: blobIdToAdd,
        },
        successMessage,
      );

      setSelectedUploadFile(null);
    } catch {
      toast.error('Failed to upload and add document');
    } finally {
      setIsUploadingManualDocument(false);
    }
  }, [
    handleCreateComponent,
    convertBlobToDocx,
    handleUploadToCollectionAndReturn,
    matterId,
    refetchMatterBlobs,
    selectedUploadFile,
    selectedUploadIsPdf,
    uploadPdfImportMode,
    uploadTargetCollectionId,
  ]);

  const handleConvertPreviousPackageToDocx = useCallback(async () => {
    if (!previousPackageBlobId) {
      toast.error('No previous HIPAA package is available to convert.');
      return;
    }

    try {
      const convertedBlob = await convertBlobToDocx({
        matterId,
        blobId: previousPackageBlobId,
      }).unwrap();

      await refetchMatterBlobs();

      await handleCreateComponent(
        {
          component_type: 'matter_blob',
          blob_id: convertedBlob.id,
        },
        'Previous HIPAA package converted to DOCX and added to the package',
      );
    } catch {
      toast.error('Failed to convert previous HIPAA package');
    }
  }, [
    convertBlobToDocx,
    handleCreateComponent,
    matterId,
    previousPackageBlobId,
    refetchMatterBlobs,
  ]);

  const renderPreviousPackageTabContent = () => {
    if (!hasPreviousPackageArtifact) {
      return (
        <div className="flex h-full items-center justify-center bg-zinc-50">
          <span className="text-sm font-medium text-zinc-500">
            No previous package is available.
          </span>
        </div>
      );
    }

    const renderPreviousPackagePreview = () => {
      if (previousPackageBlobDetail?.download_url) {
        return (
          <div
            className={cn(
              'overflow-hidden rounded-[18px] border border-zinc-200 bg-zinc-50',
              previewHeightClass,
            )}
          >
            <MemoizedIframePreview
              title="Previous HIPAA Package"
              url={previousPackageBlobDetail.download_url}
            />
          </div>
        );
      }

      if (previousPackagePreviewUrl) {
        return (
          <div
            className={cn(
              'overflow-auto rounded-[18px] border border-zinc-200 bg-zinc-50 p-2.5',
              previewHeightClass,
            )}
          >
            <MemoizedIframePreview
              title="Previous HIPAA Package"
              url={previousPackagePreviewUrl}
            />
          </div>
        );
      }

      return (
        <div
          className={cn(
            'flex items-center justify-center rounded-[18px] border border-dashed border-zinc-200 bg-zinc-50',
            previewHeightClass,
          )}
        >
          <p className="text-sm text-zinc-500">Preview unavailable.</p>
        </div>
      );
    };

    return (
      <div
        className={cn('flex h-full min-h-0', isCompactHeight ? 'gap-3' : 'gap-4')}
      >
        <div
          className={cn(
            'flex shrink-0 flex-col gap-2.5',
            documentPickerSidebarWidthClass,
          )}
        >
          <div
            className={cn(
              DOCUMENT_CARD_BASE,
              'border-indigo-300 ring-1 ring-indigo-200',
              isCompactHeight ? 'px-4 py-3 text-left' : 'px-5 py-4 text-left',
            )}
          >
            <div className="flex items-center justify-between gap-3">
              <span
                className={cn(
                  'min-w-0 flex-1 truncate font-semibold leading-5 text-zinc-900',
                  isCompactHeight ? 'text-[13px]' : 'text-[15px]',
                )}
              >
                Previous HIPAA Package
              </span>
              <span
                className={cn(
                  'rounded-full bg-emerald-50 font-semibold text-emerald-700',
                  isCompactHeight ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-[11px]',
                )}
              >
                Reused
              </span>
            </div>
          </div>

          <div
            className={cn(
              DOCUMENT_CARD_BASE,
              isCompactHeight ? 'px-4 py-3' : 'px-5 py-4',
            )}
          >
            <Button
              outline
              className="!items-center h-9 w-full justify-center rounded-[10px] border-zinc-300 bg-white px-4 text-[13px] font-medium text-zinc-800 shadow-sm"
              onClick={handleConvertPreviousPackageToDocx}
              disabled={!previousPackageBlobId || isConvertingBlobToDocx}
            >
              {isConvertingBlobToDocx && (
                <Loader2Icon className="mr-1.5 h-4 w-4 animate-spin" />
              )}
              Convert to DOCX to Edit
            </Button>

            {!previousPackageBlobId ? (
              <p className="mt-2 text-[12px] leading-5 text-zinc-500">
                Conversion unavailable for this package.
              </p>
            ) : null}
          </div>
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-2.5">
          <div
            className={cn(
              DOCUMENT_CARD_BASE,
              'flex min-h-0 flex-1 flex-col overflow-hidden',
              isCompactHeight ? 'p-3' : 'p-4',
            )}
          >
            <p
              className={cn(
                'font-semibold text-zinc-950',
                isCompactHeight ? 'text-[15px]' : 'text-base',
              )}
            >
              Preview
            </p>
            <p className="mt-1 text-[13px] leading-5 text-zinc-500">
              Review the previous package PDF as it will be reused for this
              resend.
            </p>
            <div className="mt-3">{renderPreviousPackagePreview()}</div>
          </div>
        </div>
      </div>
    );
  };

  const renderComponentTabContent = () => {
    if (!selectedComponent) return null;

    if (isOnlyOfficeEditablePackageComponent(selectedComponent)) {
      if (isLoadingConfig && !editorConfig) {
        return <LoadingPreview label="Loading document..." />;
      }

      if (editorConfig) {
        return (
          <InlineOnlyOfficeEditor
            key={selectedComponent.id}
            editorConfig={editorConfig}
            onDocumentStateChange={handleOnlyOfficeDocumentStateChange}
          />
        );
      }

      return (
        <div className="flex h-full items-center justify-center">
          <span className="text-sm font-medium text-zinc-500">
            Preparing this document for editing...
          </span>
        </div>
      );
    }

    if (selectedComponent.component_type === 'matter_blob' && selectedComponent.blob_id) {
      return (
        <div
          className={cn(
            'h-full overflow-auto bg-zinc-50',
            isCompactHeight ? 'p-3' : 'p-6',
          )}
        >
          <div
            className={cn(
              'mx-auto h-full min-h-[720px] max-w-4xl border border-zinc-200 bg-white shadow-[0_12px_40px_rgba(15,23,42,0.06)]',
              isCompactHeight ? 'rounded-[18px] p-2' : 'rounded-[24px] p-3',
            )}
          >
            <BlobDocumentPreview blobId={selectedComponent.blob_id} />
          </div>
        </div>
      );
    }

    if (selectedComponent.rendered_pdf_url) {
      return (
        <div
          className={cn(
            'h-full overflow-auto bg-zinc-50',
            isCompactHeight ? 'p-3' : 'p-6',
          )}
        >
          <div
            className={cn(
              'mx-auto h-full max-w-5xl border border-zinc-200 bg-white shadow-[0_12px_40px_rgba(15,23,42,0.06)]',
              isCompactHeight ? 'rounded-[18px] p-2' : 'rounded-[24px] p-3',
            )}
          >
            <MemoizedIframePreview
              title={getPackageComponentTitle(selectedComponent)}
              url={selectedComponent.rendered_pdf_url}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="flex h-full items-center justify-center bg-zinc-50">
        <span className="text-sm font-medium text-zinc-500">
          Preview unavailable for this document.
        </span>
      </div>
    );
  };

  const renderTemplatePicker = () => {
    const isLoadingTemplates =
      isLoadingMatterDetails || isLoadingFirmTemplates || isLoadingGlobalTemplates;

    if (isLoadingTemplates) {
      return <LoadingPreview label="Loading document templates..." />;
    }

    if (filteredDocxTemplates.length === 0) {
      return (
        <AddDocumentEmptyState
          title="No templates available"
          description="We could not find any matching templates for this library."
        />
      );
    }

    const renderSelectedTemplatePreview = () => {
      if (!selectedTemplate) {
        return (
          <div
            className={cn(
              'mt-3 flex min-h-0 flex-1 items-center justify-center rounded-[18px] border border-dashed border-zinc-200 bg-zinc-50',
            )}
          >
            <p className="text-sm text-zinc-500">Select a template to preview it.</p>
          </div>
        );
      }

      if (selectedTemplate.format === 'DOCX') {
        return (
          <div
            className={cn(
              'mt-3 flex min-h-0 flex-1 overflow-hidden rounded-[18px] border border-zinc-200 bg-zinc-50 p-2.5',
            )}
          >
            <div className="min-h-0 flex-1">
              {isLoadingTemplatePreview && !templatePreviewEditorConfig ? (
                <CompactPreviewLoading label="Loading DOCX preview..." />
              ) : null}
              {!isLoadingTemplatePreview && templatePreviewEditorConfig ? (
                <InlineOnlyOfficeEditor
                  key={`template-preview-${selectedTemplate.id}`}
                  editorConfig={templatePreviewEditorConfig}
                />
              ) : null}
              {!isLoadingTemplatePreview && !templatePreviewEditorConfig ? (
                <CompactPreviewError
                  message={
                    templatePreviewError || 'Preview unavailable for this template.'
                  }
                />
              ) : null}
            </div>
          </div>
        );
      }

      return (
        <div
          className={cn(
            'mt-3 min-h-0 flex-1 overflow-hidden rounded-[18px] border border-zinc-200 bg-zinc-50',
          )}
        >
          {selectedTemplate.previewRenderUrl ? (
            <MemoizedIframePreview
              title={selectedTemplate.name}
              url={selectedTemplate.previewRenderUrl}
            />
          ) : (
            <CompactPreviewError message="Preview unavailable for this template." />
          )}
        </div>
      );
    };

    return (
      <div
        className={cn('flex h-full min-h-0', isCompactHeight ? 'gap-3' : 'gap-4')}
      >
        <div className={cn('flex shrink-0 flex-col gap-2.5', sidebarWidthClass)}>
          <div
            className={cn(
              'flex flex-col gap-2.5 overflow-auto pr-1',
              isCompactHeight ? 'max-h-[260px]' : 'max-h-[460px]',
            )}
          >
            {libraryTemplates.map((template) => {
              const isSelected = template.id === selectedTemplate?.id;

              return (
                <button
                  key={template.id}
                  type="button"
                  onClick={() => setSelectedTemplateId(template.id)}
                  className={cn(
                    DOCUMENT_CARD_BASE,
                    isCompactHeight ? 'px-3 py-2.5 text-left transition-all' : 'px-4 py-3 text-left transition-all',
                    isSelected
                      ? 'border-indigo-300 ring-1 ring-indigo-200'
                      : 'border-zinc-200 hover:border-zinc-300',
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <span className={cn(
                      'min-w-0 flex-1 truncate font-semibold leading-5 text-zinc-900',
                      isCompactHeight ? 'text-[13px]' : 'text-sm',
                    )}>
                      {template.name}
                    </span>
                    {isSelected && (
                      <span className={cn(
                        'rounded-full bg-emerald-50 font-semibold text-emerald-700',
                        isCompactHeight ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-[11px]',
                      )}>
                        Selected
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-2.5">
          <div
            className={cn(
              DOCUMENT_CARD_BASE,
              'flex min-h-0 flex-1 flex-col overflow-hidden',
              isCompactHeight ? 'p-3' : 'p-4',
            )}
          >
            <p className={cn('font-semibold text-zinc-950', isCompactHeight ? 'text-[15px]' : 'text-base')}>
              Preview
            </p>
            {renderSelectedTemplatePreview()}
          </div>
        </div>
      </div>
    );
  };

  const renderMatterDocumentPicker = () => {
    if (isLoadingMatterBlobs) {
      return <LoadingPreview label="Loading matter documents..." />;
    }

    if (matterDocumentOptions.length === 0) {
      return (
        <AddDocumentEmptyState
          title="No matter documents available"
          description="Every document file already in the matter is already in the package, or there are no supported files yet."
        />
      );
    }

    const renderSelectedMatterDocumentPreview = () => {
      if (!selectedMatterBlob) {
        return (
          <div
            className={cn(
              'mt-3 flex min-h-0 flex-1 items-center justify-center rounded-[18px] border border-dashed border-zinc-200 bg-zinc-50',
            )}
          >
            <p className="text-sm text-zinc-500">Select a document to preview it.</p>
          </div>
        );
      }

      if (selectedMatterBlobIsWordDocument) {
        return (
          <div
            className={cn(
              'mt-3 flex min-h-0 flex-1 overflow-hidden rounded-[18px] border border-zinc-200 bg-zinc-50 p-2.5',
            )}
          >
            <div className="min-h-0 flex-1">
              {isLoadingMatterBlobPreview && !matterBlobPreviewEditorConfig ? (
                <CompactPreviewLoading label="Loading DOCX preview..." />
              ) : null}
              {!isLoadingMatterBlobPreview && matterBlobPreviewEditorConfig ? (
                <InlineOnlyOfficeEditor
                  key={`matter-blob-preview-${selectedMatterBlob.id}`}
                  editorConfig={matterBlobPreviewEditorConfig}
                />
              ) : null}
              {!isLoadingMatterBlobPreview && !matterBlobPreviewEditorConfig ? (
                <CompactPreviewError
                  message={
                    matterBlobPreviewError ||
                    'Preview unavailable for this document.'
                  }
                />
              ) : null}
            </div>
          </div>
        );
      }

      return (
        <div
          className={cn(
            'mt-3 min-h-0 flex-1 overflow-hidden rounded-[18px] border border-zinc-200 bg-zinc-50',
          )}
        >
          {selectedMatterBlobPreviewUrl ? (
            <MemoizedIframePreview
              title={
                selectedMatterBlob.name ||
                selectedMatterBlob.original_filename ||
                'Matter document'
              }
              url={selectedMatterBlobPreviewUrl}
              assumePdf={selectedMatterBlobIsPdfDocument}
            />
          ) : (
            <CompactPreviewError message="Preview unavailable for this document." />
          )}
        </div>
      );
    };

    return (
      <div
        className={cn('flex h-full min-h-0', isCompactHeight ? 'gap-3' : 'gap-4')}
      >
        <div className={cn('flex shrink-0 flex-col gap-2.5', sidebarWidthClass)}>
          <div className="flex items-center justify-between gap-3">
            <Button
              outline
              onClick={openMatterDocumentPicker}
              className={cn(isCompactHeight && 'h-9 px-4 text-sm')}
            >
              <FolderOpenIcon className="mr-1.5 h-4 w-4" />
              Select File
            </Button>
            <span className="text-sm text-zinc-500">
              {matterDocumentOptions.length} file
              {matterDocumentOptions.length === 1 ? '' : 's'} available
            </span>
          </div>
          {selectedMatterBlob ? (
            <button
              type="button"
              onClick={openMatterDocumentPicker}
              className={cn(
                DOCUMENT_CARD_BASE,
                'w-full text-left transition-all',
                isCompactHeight ? 'px-3 py-2.5' : 'px-4 py-3',
                'border-indigo-300 ring-1 ring-indigo-200 hover:border-indigo-400',
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <span
                  className={cn(
                    'min-w-0 flex-1 break-all font-semibold leading-5 text-zinc-900 line-clamp-2',
                    isCompactHeight ? 'text-[12px]' : 'text-[13px]',
                  )}
                >
                  {selectedMatterBlob.name ||
                    selectedMatterBlob.original_filename ||
                    'Matter document'}
                </span>
                <span
                  className={cn(
                    'rounded-full bg-emerald-50 font-semibold text-emerald-700',
                    isCompactHeight
                      ? 'px-1.5 py-0.5 text-[10px]'
                      : 'px-2 py-0.5 text-[11px]',
                  )}
                >
                  Selected
                </span>
              </div>
            </button>
          ) : null}
          {selectedMatterBlobIsPdfDocument ? (
            <>
              <div className="mt-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
                  PDF Handling
                </p>
                <div className="mt-2 flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setMatterPdfImportMode('as_pdf');
                      handleAddMatterDocument('as_pdf').catch(() => {
                        toast.error('Failed to add document');
                      });
                    }}
                    disabled={isAddingComponent || isConvertingBlobToDocx}
                    className={cn(
                      'flex w-full items-center justify-center rounded-[12px] border px-3 py-2.5 text-[13px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60',
                      matterPdfImportMode === 'as_pdf'
                        ? 'border-zinc-300 bg-white text-zinc-900 shadow-[0_1px_2px_rgba(15,23,42,0.06)]'
                        : 'border-zinc-200 bg-zinc-50/70 text-zinc-600 hover:border-zinc-300 hover:bg-white hover:text-zinc-800',
                    )}
                  >
                    Add as PDF
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setMatterPdfImportMode('convert_to_docx');
                      handleAddMatterDocument('convert_to_docx').catch(() => {
                        toast.error('Failed to add document');
                      });
                    }}
                    disabled={isAddingComponent || isConvertingBlobToDocx}
                    className={cn(
                      'flex w-full items-center justify-center rounded-[12px] border px-3 py-2.5 text-[13px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60',
                      matterPdfImportMode === 'convert_to_docx'
                        ? 'border-indigo-300 bg-indigo-50 text-indigo-700 shadow-[0_1px_2px_rgba(79,70,229,0.12)]'
                        : 'border-zinc-200 bg-zinc-50/70 text-zinc-600 hover:border-zinc-300 hover:bg-white hover:text-zinc-800',
                    )}
                  >
                    Convert to DOCX
                  </button>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (selectedMatterBlob) {
                    setExtractionBlob(selectedMatterBlob);
                  }
                }}
                className={cn(
                  'mt-3 flex w-full items-center justify-center gap-2 rounded-[14px] border border-zinc-200 bg-white px-4 text-sm font-medium text-zinc-700 shadow-sm transition-colors hover:border-zinc-300 hover:bg-zinc-50',
                  isCompactHeight ? 'py-2.5' : 'py-3.5',
                )}
              >
                <ScissorsIcon className="h-4 w-4 text-amber-700" />
                Extract Pages
              </button>
            </>
          ) : null}

          {selectedMatterBlobSupportsSignatureCapture ? (
            <div className={cn(selectedMatterBlobIsPdfDocument ? 'mt-2' : 'mt-4', 'flex flex-col gap-2')}>
              {!selectedMatterBlobIsPdfDocument ? (
                <p className="px-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400">
                  Signature
                </p>
              ) : null}
              <button
                type="button"
                onClick={() => {
                  setIsSignatureCaptureOpen(true);
                }}
                className={cn(
                  'flex w-full items-center justify-center gap-2 rounded-[12px] border border-zinc-200 bg-white px-3 py-2.5 text-[13px] font-medium text-zinc-700 shadow-sm transition-colors hover:border-zinc-300 hover:bg-zinc-50',
                )}
              >
                <ScissorsIcon className="h-4 w-4 text-indigo-600" />
                Clip HIPAA Signature
              </button>
            </div>
          ) : null}
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-2.5">
          <div
            className={cn(
              DOCUMENT_CARD_BASE,
              'flex min-h-0 flex-1 flex-col overflow-hidden',
              isCompactHeight ? 'p-3' : 'p-4',
            )}
          >
            <p className={cn('font-semibold text-zinc-950', isCompactHeight ? 'text-[15px]' : 'text-base')}>
              Preview
            </p>
            {selectedMatterBlobIsPdfDocument &&
            matterPdfImportMode === 'convert_to_docx' ? (
              <p className="mt-1 text-[13px] leading-5 text-zinc-500">
                This PDF will be converted into a DOCX and added as an editable
                package document.
              </p>
            ) : null}
            {renderSelectedMatterDocumentPreview()}
          </div>
        </div>
      </div>
    );
  };

  const renderStaticDocumentPicker = () => {
    if (staticDocumentOptions.length === 0) {
      return (
        <AddDocumentEmptyState
          title="No static documents available"
          description="We could not find any static documents for this matter."
        />
      );
    }

    const selectedStaticDocumentName = selectedStaticDocument
      ? StaticDocumentLabels[selectedStaticDocument.tag]
      : null;

    let previewContent: React.ReactNode = (
      <div
        className={cn(
          'mt-3 flex min-h-0 flex-1 items-center justify-center rounded-[18px] border border-dashed border-zinc-200 bg-zinc-50 px-6 text-center',
        )}
      >
        <p className="text-sm text-zinc-500">
          Select a static document to review it here.
        </p>
      </div>
    );

    if (selectedStaticDocument) {
      previewContent = selectedStaticDocument.previewUrl ? (
        <div
          className={cn(
            'mt-3 min-h-0 flex-1 overflow-hidden rounded-[18px] border border-zinc-200 bg-zinc-50',
          )}
        >
          {isLoadingStaticPreview ? (
            <CompactPreviewLoading label="Loading static document preview..." />
          ) : null}
          {!isLoadingStaticPreview && staticPreviewObjectUrl ? (
            <MemoizedIframePreview
              title={selectedStaticDocumentName || 'Static document'}
              url={staticPreviewObjectUrl}
              assumePdf
            />
          ) : null}
          {!isLoadingStaticPreview &&
          !staticPreviewObjectUrl &&
          staticPreviewError ? (
            <CompactPreviewError message={staticPreviewError} />
          ) : null}
        </div>
      ) : (
        <div
          className={cn(
            'mt-3 flex min-h-0 flex-1 items-center justify-center rounded-[18px] border border-dashed border-zinc-200 bg-zinc-50 px-6 text-center',
          )}
        >
          <div className="space-y-2">
            <p className="text-sm font-medium text-zinc-900">
              {selectedStaticDocumentName}
            </p>
            <p className="text-sm leading-6 text-zinc-500">
              Preview unavailable for this static document. You can still add it
              to the package.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div
        className={cn('flex h-full min-h-0', isCompactHeight ? 'gap-3' : 'gap-4')}
      >
        <div className={cn('flex shrink-0 flex-col gap-2.5', sidebarWidthClass)}>
          <div
            className={cn(
              'flex flex-col gap-2.5 overflow-auto pr-1',
              isCompactHeight ? 'max-h-[260px]' : 'max-h-[460px]',
            )}
          >
            {staticDocumentOptions.map((document) => {
              const isSelected = document.tag === selectedStaticDocumentTag;

              return (
                <button
                  key={document.tag}
                  type="button"
                  onClick={() => setSelectedStaticDocumentTag(document.tag)}
                  className={cn(
                    DOCUMENT_CARD_BASE,
                    isCompactHeight
                      ? 'px-3 py-2.5 text-left transition-all'
                      : 'px-4 py-3 text-left transition-all',
                    isSelected
                      ? 'border-indigo-300 ring-1 ring-indigo-200'
                      : 'border-zinc-200 hover:border-zinc-300',
                  )}
                >
                  <div className="space-y-1.5">
                    <div className="flex items-start justify-between gap-3">
                      <span
                        className={cn(
                          'min-w-0 flex-1 font-semibold leading-5 text-zinc-900',
                          isCompactHeight ? 'text-[13px]' : 'text-sm',
                        )}
                      >
                        {StaticDocumentLabels[document.tag]}
                      </span>
                      {isSelected ? (
                        <span
                          className={cn(
                            'rounded-full bg-emerald-50 font-semibold text-emerald-700',
                            isCompactHeight
                              ? 'px-1.5 py-0.5 text-[10px]'
                              : 'px-2 py-0.5 text-[11px]',
                          )}
                        >
                          Selected
                        </span>
                      ) : null}
                    </div>
                    <p
                      className={cn(
                        'leading-5 text-zinc-500',
                        isCompactHeight ? 'text-[11px]' : 'text-[12px]',
                      )}
                    >
                      {document.description}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-2.5">
          <div
            className={cn(
              DOCUMENT_CARD_BASE,
              'flex min-h-0 flex-1 flex-col overflow-hidden',
              isCompactHeight ? 'p-3' : 'p-4',
            )}
          >
            <p
              className={cn(
                'font-semibold text-zinc-950',
                isCompactHeight ? 'text-[15px]' : 'text-base',
              )}
            >
              Preview
            </p>
            {previewContent}
          </div>
        </div>
      </div>
    );
  };

  const renderPdfMergeFlow = () => {
    if (isLoadingMatterBlobs) {
      return <LoadingPreview label="Loading signed PDFs..." />;
    }

    if (signedPdfOptions.length === 0) {
      return (
        <AddDocumentEmptyState
          title="No signed PDF found"
          description="Add a signed HIPAA authorization PDF to the matter first, then come back to merge it into this package."
        />
      );
    }

    const noTemplatesAvailable = mergeAvailableLibraries.length === 0;
    const signedPreviewHeightClass = isCompactHeight
      ? 'h-[320px] xl:h-[380px]'
      : 'h-[460px] xl:h-[560px]';
    let mergeTemplatePreviewContent: React.ReactNode;
    if (selectedMergeTemplate?.previewRenderUrl) {
      mergeTemplatePreviewContent = (
        <MemoizedIframePreview
          url={selectedMergeTemplate.previewRenderUrl}
          title={selectedMergeTemplate.name}
        />
      );
    } else if (selectedMergeTemplate) {
      mergeTemplatePreviewContent = (
        <div className="flex h-full items-center justify-center px-4 text-center">
          <p className="text-sm text-zinc-500">
            Preview unavailable for this PDF template.
          </p>
        </div>
      );
    } else {
      mergeTemplatePreviewContent = (
        <div className="flex h-full items-center justify-center px-4 text-center">
          <p className="text-sm text-zinc-500">
            Select a PDF authorization template to preview it here.
          </p>
        </div>
      );
    }

    return (
      <div
        className={cn(
          'flex h-full min-h-0 flex-col',
          isCompactHeight ? 'gap-3' : 'gap-4',
        )}
      >
        <div
          className={cn(
            'grid min-h-0 grid-cols-2',
            isCompactHeight ? 'gap-3' : 'gap-4',
          )}
        >
          <div
            className={cn(
              DOCUMENT_CARD_BASE,
              'min-h-0 overflow-hidden',
              isCompactHeight ? 'p-3' : 'p-4',
            )}
          >
            <div className="flex items-center gap-2">
              <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-indigo-50 px-2 text-[11px] font-semibold text-indigo-700">
                1
              </span>
              <p
                className={cn(
                  'font-semibold text-zinc-950',
                  isCompactHeight ? 'text-[15px]' : 'text-base',
                )}
              >
                Pick signed authorization PDF
              </p>
            </div>
            <p className="mt-1 text-[13px] leading-5 text-zinc-500">
              Choose the signed PDF already on file in the matter.
            </p>
            <div className="mt-3 flex items-center justify-between gap-3">
              <Button
                outline
                onClick={openSignedPdfPicker}
                className={cn(isCompactHeight && 'h-9 px-4 text-sm')}
              >
                <FolderOpenIcon className="mr-1.5 h-4 w-4" />
                Select File
              </Button>
              <span className="text-sm text-zinc-500">
                {signedPdfOptions.length} PDF
                {signedPdfOptions.length === 1 ? '' : 's'} available
              </span>
            </div>
            {selectedSignedBlob ? (
              <button
                type="button"
                onClick={openSignedPdfPicker}
                className={cn(
                  DOCUMENT_CARD_BASE,
                  'mt-3 w-full text-left transition-all',
                  isCompactHeight ? 'px-3 py-2.5' : 'px-4 py-3',
                  'border-indigo-300 ring-1 ring-indigo-200 hover:border-indigo-400',
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <span
                    className={cn(
                      'min-w-0 flex-1 break-all font-semibold leading-5 text-zinc-900 line-clamp-2',
                      isCompactHeight ? 'text-[12px]' : 'text-[13px]',
                    )}
                  >
                    {selectedSignedBlob.name ||
                      selectedSignedBlob.original_filename ||
                      'Signed PDF'}
                  </span>
                  <span
                    className={cn(
                      'rounded-full bg-emerald-50 font-semibold text-emerald-700',
                      isCompactHeight
                        ? 'px-1.5 py-0.5 text-[10px]'
                        : 'px-2 py-0.5 text-[11px]',
                    )}
                  >
                    Selected
                  </span>
                </div>
              </button>
            ) : null}
            <div
              className={cn(
                'mt-3 overflow-hidden rounded-[18px] border border-zinc-200 bg-zinc-50',
                signedPreviewHeightClass,
              )}
            >
              {selectedSignedBlob?.download_url ? (
                <MemoizedIframePreview
                  url={selectedSignedBlob.download_url}
                  title={
                    selectedSignedBlob.name ||
                    selectedSignedBlob.original_filename ||
                    'Signed authorization PDF'
                  }
                />
              ) : (
                <div className="flex h-full items-center justify-center px-4 text-center">
                  <p className="text-sm text-zinc-500">
                    Select a signed PDF to preview it here.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div
            className={cn(
              DOCUMENT_CARD_BASE,
              'min-h-0 overflow-hidden',
              isCompactHeight ? 'p-3' : 'p-4',
            )}
          >
            <div className="flex items-center gap-2">
              <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-amber-50 px-2 text-[11px] font-semibold text-amber-700">
                2
              </span>
              <p
                className={cn(
                  'font-semibold text-zinc-950',
                  isCompactHeight ? 'text-[15px]' : 'text-base',
                )}
              >
                Pick merge template
              </p>
            </div>
            <p className="mt-1 text-[13px] leading-5 text-zinc-500">
              Choose the PDF authorization template that should be merged with
              the signed file.
            </p>
            {noTemplatesAvailable ? (
              <div
                className={cn(
                  'mt-3 flex items-center justify-center rounded-[18px] border border-dashed border-zinc-200 bg-zinc-50 text-center',
                  isCompactHeight ? 'min-h-[220px] px-4 py-6' : 'min-h-[280px] px-6 py-8',
                )}
              >
                <div>
                  <p className="text-base font-semibold text-zinc-900">
                    No PDF merge template available
                  </p>
                  <p className="mt-2 text-sm leading-6 text-zinc-500">
                    This library does not have an authorization PDF template
                    available for merge.
                  </p>
                </div>
              </div>
            ) : (
              <div
                className={cn(
                  'mt-3 flex flex-col gap-2.5 overflow-auto pr-1',
                  isCompactHeight ? 'max-h-[180px]' : 'max-h-[220px]',
                )}
              >
                {mergeLibraryTemplates.map((template) => {
                  const isSelected = template.id === selectedMergeTemplate?.id;

                  return (
                    <button
                      key={template.id}
                      type="button"
                      onClick={() => setSelectedMergeTemplateId(template.id)}
                      className={cn(
                        DOCUMENT_CARD_BASE,
                        isCompactHeight
                          ? 'px-3 py-2.5 text-left transition-all'
                          : 'px-4 py-3 text-left transition-all',
                        isSelected
                          ? 'border-indigo-300 ring-1 ring-indigo-200'
                          : 'border-zinc-200 hover:border-zinc-300',
                      )}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <span
                          className={cn(
                            'min-w-0 flex-1 truncate font-semibold leading-5 text-zinc-900',
                            isCompactHeight ? 'text-[13px]' : 'text-sm',
                          )}
                        >
                          {template.name}
                        </span>
                        {isSelected && (
                          <span
                            className={cn(
                              'rounded-full bg-emerald-50 font-semibold text-emerald-700',
                              isCompactHeight
                                ? 'px-1.5 py-0.5 text-[10px]'
                                : 'px-2 py-0.5 text-[11px]',
                            )}
                          >
                            Selected
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            <div
              className={cn(
                'mt-3 overflow-hidden rounded-[18px] border border-zinc-200 bg-zinc-50',
                signedPreviewHeightClass,
              )}
            >
              {mergeTemplatePreviewContent}
            </div>
          </div>
        </div>

        <div
          className={cn(
            DOCUMENT_CARD_BASE,
            isCompactHeight ? 'px-3 py-2.5' : 'px-4 py-3.5',
          )}
        >
          <p className="text-sm font-semibold text-zinc-900">Merge summary</p>
          <div className="mt-2 grid gap-2.5 md:grid-cols-2">
            <div className="rounded-[16px] border border-zinc-200 bg-zinc-50 px-3 py-2.5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
                Signed PDF
              </p>
              <p className="mt-1 truncate text-sm font-semibold text-zinc-900">
                {selectedSignedBlob?.name ||
                  selectedSignedBlob?.original_filename ||
                  'None selected'}
              </p>
            </div>
            <div className="rounded-[16px] border border-zinc-200 bg-zinc-50 px-3 py-2.5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
                Merge template
              </p>
              <p className="mt-1 truncate text-sm font-semibold text-zinc-900">
                {selectedMergeTemplate?.name || 'None selected'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderManualUploadPicker = () => {
    const renderUploadedDocumentPreview = () => {
      if (!selectedUploadFile) {
        return (
          <div
            className={cn(
              'mt-3 flex items-center justify-center rounded-[18px] border border-dashed border-zinc-200 bg-zinc-50',
              previewHeightClass,
            )}
          >
            <p className="text-sm text-zinc-500">
              Select a PDF or Word document to preview it here.
            </p>
          </div>
        );
      }

      if (selectedUploadIsPdf && selectedUploadPreviewUrl) {
        return (
          <div
            className={cn(
              'mt-3 overflow-hidden rounded-[18px] border border-zinc-200 bg-zinc-50 p-2.5',
              previewHeightClass,
            )}
          >
            <iframe
              title="Uploaded document preview"
              src={selectedUploadPreviewUrl}
              className="h-full w-full rounded-[12px] bg-white"
            />
          </div>
        );
      }

      return (
        <div
          className={cn(
              'mt-3 flex items-center justify-center rounded-[18px] border border-zinc-200 bg-zinc-50 p-6',
              previewHeightClass,
            )}
          >
          <div className="max-w-sm text-center">
            <p className="text-sm font-semibold text-zinc-900">
              {selectedUploadFile.name}
            </p>
            <p className="mt-2 text-sm leading-6 text-zinc-500">
              {selectedUploadIsWord
                ? 'Word documents become fully previewable after upload, when they are added to the package.'
                : 'This file will be uploaded into the matter and added to the package as-is.'}
            </p>
          </div>
        </div>
      );
    };

    return (
      <div
        className={cn('flex h-full min-h-0', isCompactHeight ? 'gap-3' : 'gap-4')}
      >
        <div className={cn('flex shrink-0 flex-col gap-2.5', sidebarWidthClass)}>
          <div className={cn(DOCUMENT_CARD_BASE, isCompactHeight ? 'px-3 py-2.5' : 'px-4 py-4')}>
            <p className="text-sm font-semibold text-zinc-900">
              Upload from your computer
            </p>
            <p className="mt-1.5 text-[13px] leading-5 text-zinc-500">
              Add a single PDF or Word document and include it as one component
              in this package.
            </p>

            <button
              type="button"
              onClick={handlePickUploadFile}
              className={cn(
                'mt-4 flex w-full items-center justify-center gap-2 rounded-[14px] border border-dashed border-zinc-300 bg-zinc-50 px-4 text-sm font-medium text-zinc-700 transition-colors hover:border-zinc-400 hover:bg-zinc-100',
                isCompactHeight ? 'py-2.5' : 'py-4',
              )}
            >
              <UploadIcon className="h-4 w-4" />
              {selectedUploadFile ? 'Replace File' : 'Choose File'}
            </button>

            <input
              ref={uploadInputRef}
              type="file"
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              className="hidden"
              onChange={handleUploadFileChange}
            />

            {selectedUploadFile ? (
              <div className={cn(
                'mt-4 rounded-[14px] border border-zinc-200 bg-white px-3',
                isCompactHeight ? 'py-2.5' : 'py-3',
              )}>
                <p className="break-all text-[13px] font-semibold leading-5 text-zinc-900">
                  {selectedUploadFile.name}
                </p>
                <p className="mt-1 text-[12px] text-zinc-500">
                  {(selectedUploadFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            ) : null}

            {selectedUploadIsPdf ? (
              <PdfImportModeSelector
                value={uploadPdfImportMode}
                onChange={setUploadPdfImportMode}
              />
            ) : null}
          </div>
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-2.5">
          <div
            className={cn(
              DOCUMENT_CARD_BASE,
              'min-h-0 flex-1 overflow-hidden',
              isCompactHeight ? 'p-3' : 'p-4',
            )}
          >
            <p className={cn('font-semibold text-zinc-950', isCompactHeight ? 'text-[15px]' : 'text-base')}>
              Preview
            </p>
            {selectedUploadIsPdf && uploadPdfImportMode === 'convert_to_docx' ? (
              <p className="mt-1 text-[13px] leading-5 text-zinc-500">
                We will upload the source PDF, convert it to DOCX on the server,
                and add the editable DOCX to this package.
              </p>
            ) : null}
            {renderUploadedDocumentPreview()}
          </div>
        </div>
      </div>
    );
  };

  const renderAddDocumentFlow = () => {
    const shouldShowLibraryToolbar =
      addDocumentKind === 'coversheet' || addDocumentKind === 'authorization';
    const currentLibraryOptions =
      addDocumentKind === 'authorization' && authorizationMode === 'pdf_merge'
        ? mergeAvailableLibraries
        : availableLibraries;

    let headerTitle = 'Add a Matter Document';
    if (addDocumentKind === 'coversheet') {
      headerTitle = 'Add a Coversheet';
    } else if (addDocumentKind === 'authorization') {
      headerTitle = 'Add an Authorization';
    } else if (addDocumentKind === 'static_document') {
      headerTitle = 'Add a Static Document';
    } else if (addDocumentKind === 'manual_upload') {
      headerTitle = 'Upload a Document';
    }

    let headerDescription =
      'Choose any file that already exists in the matter and add it into this package.';
    if (addDocumentKind === 'coversheet') {
      headerDescription =
        'Choose the library, then pick the exact coversheet to add.';
    } else if (addDocumentKind === 'authorization') {
      headerDescription =
        authorizationMode === 'pdf_merge'
          ? 'Select the signed authorization PDF on file, then choose the PDF authorization template to merge with it.'
          : 'Choose how you want to add the authorization, then pick the exact file.';
    } else if (addDocumentKind === 'static_document') {
      headerDescription =
        'Choose a static document, such as a Texas affidavit, to include in this package.';
    } else if (addDocumentKind === 'manual_upload') {
      headerDescription =
        'Upload a PDF or Word document from your computer and add it to this package.';
    }

    let documentTypeBadge: string | null = 'DOCX';
    if (
      addDocumentKind === 'authorization' &&
      authorizationMode === 'pdf_merge'
    ) {
      documentTypeBadge = null;
    } else if (addDocumentKind === 'matter_document') {
      documentTypeBadge = null;
    } else if (addDocumentKind === 'static_document') {
      documentTypeBadge = null;
    } else if (addDocumentKind === 'manual_upload') {
      documentTypeBadge = selectedUploadBadgeLabel;
    }

    let addFlowBody = renderTemplatePicker();
    if (addDocumentKind === 'matter_document') {
      addFlowBody = renderMatterDocumentPicker();
    } else if (addDocumentKind === 'static_document') {
      addFlowBody = renderStaticDocumentPicker();
    } else if (addDocumentKind === 'manual_upload') {
      addFlowBody = renderManualUploadPicker();
    } else if (
      addDocumentKind === 'authorization' &&
      authorizationMode === 'pdf_merge'
    ) {
      addFlowBody = renderPdfMergeFlow();
    }

    return (
      <div
        className={cn(
          'flex min-h-0 flex-1 flex-col bg-[#f6f7fb]',
          isCompactHeight ? 'gap-2.5 px-3 py-2.5' : 'gap-4 px-5 py-4',
        )}
      >
        <div className={cn(DOCUMENT_CARD_BASE, isCompactHeight ? 'px-3 py-2' : 'px-4 py-3')}>
          <div className={cn('flex flex-wrap items-center justify-between', isCompactHeight ? 'gap-2' : 'gap-3')}>
            <div
              className={cn(
                'flex-1',
                isCompactHeight ? 'max-w-[180px] min-w-[140px]' : 'max-w-[280px] min-w-[220px]',
              )}
            >
              <div className="flex items-center gap-2">
                <p className={cn('font-semibold text-zinc-950', isCompactHeight ? 'text-[15px]' : 'text-[17px]')}>
                  {headerTitle}
                </p>
                {documentTypeBadge && (
                  <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-700">
                    {documentTypeBadge}
                  </span>
                )}
              </div>
              <p
                className={cn(
                  'text-zinc-500',
                  isCompactHeight
                    ? 'mt-0.5 line-clamp-1 text-[11px] leading-4'
                    : 'mt-1 text-[13px] leading-5',
                )}
              >
                {headerDescription}
              </p>
            </div>

            <div
              className={cn(
                'flex flex-1 flex-wrap justify-end gap-2',
                isCompactHeight ? 'min-w-[320px]' : 'min-w-[420px]',
              )}
            >
              <ToolbarGroup compact={isCompactHeight} label="Document">
                <FlowOptionButton
                  compact={isCompactHeight}
                  icon={FilesIcon}
                  isSelected={addDocumentKind === 'coversheet'}
                  label="Coversheet"
                  onClick={() => setAddDocumentKind('coversheet')}
                />
                <FlowOptionButton
                  compact={isCompactHeight}
                  icon={DocumentTextIcon}
                  isSelected={addDocumentKind === 'authorization'}
                  label="Authorization"
                  onClick={() => setAddDocumentKind('authorization')}
                />
                <FlowOptionButton
                  compact={isCompactHeight}
                  icon={FolderOpenIcon}
                  isSelected={addDocumentKind === 'matter_document'}
                  label="Matter Document"
                  onClick={() => setAddDocumentKind('matter_document')}
                />
                <FlowOptionButton
                  compact={isCompactHeight}
                  icon={BuildingOffice2Icon}
                  isSelected={addDocumentKind === 'static_document'}
                  label="Static Document"
                  onClick={() => setAddDocumentKind('static_document')}
                />
                <FlowOptionButton
                  compact={isCompactHeight}
                  icon={UploadIcon}
                  isSelected={addDocumentKind === 'manual_upload'}
                  label=""
                  ariaLabel="Upload"
                  onClick={() => setAddDocumentKind('manual_upload')}
                />
              </ToolbarGroup>

              {addDocumentKind === 'authorization' && (
                <ToolbarGroup compact={isCompactHeight} label="Mode">
                  <ModeOptionButton
                    compact={isCompactHeight}
                    isSelected={authorizationMode === 'standard_docx'}
                    label="Standard DOCX"
                    onClick={() => setAuthorizationMode('standard_docx')}
                  />
                  <ModeOptionButton
                    compact={isCompactHeight}
                    badge="Break glass"
                    isSelected={authorizationMode === 'pdf_merge'}
                    label="PDF Merge"
                    onClick={() => setAuthorizationMode('pdf_merge')}
                    tone="warning"
                  />
                </ToolbarGroup>
              )}

              {shouldShowLibraryToolbar && currentLibraryOptions.length > 0 && (
                  <ToolbarGroup compact={isCompactHeight} label="Library">
                    {currentLibraryOptions.map((library) => (
                      <LibraryPillButton
                        compact={isCompactHeight}
                        key={library}
                        isAuto={library === 'lighthouse'}
                        isSelected={selectedLibrary === library}
                        label={getLibraryLabel(library)}
                        onClick={() => setSelectedLibrary(library)}
                      />
                    ))}
                  </ToolbarGroup>
                )}
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1">{addFlowBody}</div>
      </div>
    );
  };

  let addActionLabel = 'Add Matter Document';
  if (addDocumentKind === 'coversheet') {
    addActionLabel = 'Add Coversheet';
  } else if (addDocumentKind === 'authorization') {
    addActionLabel =
      authorizationMode === 'pdf_merge'
        ? 'Add Merged Authorization'
        : 'Add Authorization';
  } else if (addDocumentKind === 'static_document') {
    addActionLabel = 'Add Static Document';
  } else if (
    addDocumentKind === 'matter_document' &&
    selectedMatterBlobIsPdfDocument &&
    matterPdfImportMode === 'convert_to_docx'
  ) {
    addActionLabel = 'Convert to DOCX and Add';
  } else if (addDocumentKind === 'manual_upload') {
    addActionLabel =
      selectedUploadIsPdf && uploadPdfImportMode === 'convert_to_docx'
        ? 'Upload, Convert, and Add'
        : 'Upload and Add Document';
  }

  const addActionDisabled =
    isAddingComponent ||
    isConvertingBlobToDocx ||
    isUploadingManualDocument ||
    (addDocumentKind === 'coversheet' && !selectedTemplate) ||
    (addDocumentKind === 'authorization' &&
      authorizationMode === 'standard_docx' &&
      !selectedTemplate) ||
    (addDocumentKind === 'authorization' &&
      authorizationMode === 'pdf_merge' &&
      (!selectedSignedBlob || !selectedMergeTemplate)) ||
    (addDocumentKind === 'static_document' && !selectedStaticDocumentTag) ||
    (addDocumentKind === 'matter_document' && !selectedMatterBlob) ||
    (addDocumentKind === 'manual_upload' &&
      (!selectedUploadFile || !uploadTargetCollectionId));

  const handleAddAction = useCallback(async () => {
    try {
      if (addDocumentKind === 'coversheet') {
        await handleAddTemplate();
        return;
      }

      if (addDocumentKind === 'matter_document') {
        await handleAddMatterDocument();
        return;
      }

      if (addDocumentKind === 'static_document') {
        await handleAddStaticDocument();
        return;
      }

      if (addDocumentKind === 'manual_upload') {
        await handleAddUploadedDocument();
        return;
      }

      if (authorizationMode === 'pdf_merge') {
        await handleAddPdfMerge();
        return;
      }

      await handleAddTemplate();
    } catch {
      toast.error('Failed to add document');
    }
  }, [
    addDocumentKind,
    authorizationMode,
    handleAddMatterDocument,
    handleAddPdfMerge,
    handleAddStaticDocument,
    handleAddTemplate,
    handleAddUploadedDocument,
  ]);

  const openSignedPdfPicker = useCallback(() => {
    setSignedPdfPickerSelectionIds(
      selectedSignedBlobId ? [selectedSignedBlobId] : [],
    );
    setIsSignedPdfPickerOpen(true);
  }, [selectedSignedBlobId]);

  const openMatterDocumentPicker = useCallback(() => {
    setMatterDocumentPickerSelectionIds(
      selectedMatterBlobId ? [selectedMatterBlobId] : [],
    );
    setIsMatterDocumentPickerOpen(true);
  }, [selectedMatterBlobId]);

  const handleMatterDocumentPickerConfirm = useCallback(() => {
    setSelectedMatterBlobId(matterDocumentPickerSelectionIds[0] ?? null);
    setIsMatterDocumentPickerOpen(false);
  }, [matterDocumentPickerSelectionIds]);

  const handleSignedPdfPickerConfirm = useCallback(() => {
    setSelectedSignedBlobId(signedPdfPickerSelectionIds[0] ?? null);
    setIsSignedPdfPickerOpen(false);
  }, [signedPdfPickerSelectionIds]);

  let modalBodyContent = renderComponentTabContent();
  if (activeTab.type === 'add') {
    modalBodyContent = renderAddDocumentFlow();
  } else if (activeTab.type === 'previous-package') {
    modalBodyContent = renderPreviousPackageTabContent();
  }

  let footerDescription =
    'Changes save automatically and apply to the final submission.';
  if (activeTab.type === 'add') {
    if (
      addDocumentKind === 'authorization' &&
      authorizationMode === 'pdf_merge'
    ) {
      footerDescription =
        'This keeps the signed PDF intact and appends the request package behind it.';
    } else if (addDocumentKind === 'static_document') {
      footerDescription = 'Adds a static document to this package.';
    } else if (addDocumentKind === 'manual_upload') {
      footerDescription =
        'Uploads the file into the matter and adds it to this package.';
    } else {
      footerDescription = 'Adds a new document to this package.';
    }
  } else if (activeTab.type === 'previous-package') {
    footerDescription =
      'The previous HIPAA package stays in use unless you add or replace a document.';
  }

  if (!isOpen) return null;

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={handleClose}
        size="5xl"
        className="sm:max-w-[min(96vw,1800px)]"
        zIndex={Z_INDEX_MODAL_NESTED}
        nested
      >
        <DialogBody
          className={cn(
            'mt-0 !p-0',
            isCompactHeight
              ? 'h-[96vh] max-h-[96vh]'
              : 'h-[min(94vh,1400px)] max-h-[min(94vh,1400px)]',
          )}
        >
          <div className="flex h-full flex-col overflow-hidden">
            <div
              className={cn(
                'flex items-center gap-3 border-b border-zinc-200 px-6',
                isCompactHeight ? 'py-2' : 'py-4',
              )}
            >
              <DialogTitle
                className={cn(
                  'font-semibold text-zinc-900',
                  isCompactHeight ? 'text-base' : 'text-lg',
                )}
              >
                Edit Documents
              </DialogTitle>
              <button
                type="button"
                onClick={handleClose}
                className={cn(
                  'ml-auto rounded-md text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-700',
                  isCompactHeight ? 'p-1' : 'p-1.5',
                )}
              >
                <XIcon className={cn(isCompactHeight ? 'h-[18px] w-[18px]' : 'h-5 w-5')} />
              </button>
            </div>

            <div
              className={cn(
                'flex items-end gap-1 border-b border-zinc-200 px-6 overflow-x-auto',
                isCompactHeight ? 'min-h-[34px]' : '',
              )}
            >
              {isPreviousPackageOnlyState && (
                <button
                  type="button"
                  onClick={() => setActiveTab({ type: 'previous-package' })}
                  className={cn(
                    '-mb-px flex items-center gap-2 border-b-2 font-medium transition-colors',
                    isCompactHeight ? 'px-2 py-1 text-[11px]' : 'px-3 py-3 text-sm',
                    activeTab.type === 'previous-package'
                      ? 'border-indigo-600 text-indigo-700'
                      : 'border-transparent text-zinc-500 hover:border-zinc-300 hover:text-zinc-700',
                  )}
                >
                  <FilesIcon className="h-4 w-4" />
                  <span
                    className={cn(
                      isCompactHeight ? 'max-w-[180px] truncate' : 'max-w-[220px] truncate',
                    )}
                  >
                    Previous Package
                  </span>
                </button>
              )}

              {visibleComponents.map((component) => {
                const isSelected =
                  activeTab.type === 'component' &&
                  component.id === activeTab.componentId;

                return (
                  <div
                    key={component.id}
                    className={cn(
                      '-mb-px flex items-center gap-1 border-b-2 font-medium transition-colors',
                      isCompactHeight ? 'px-2 py-1 text-[11px]' : 'px-3 py-3 text-sm',
                      isSelected
                        ? 'border-indigo-600 text-indigo-700'
                        : 'border-transparent text-zinc-500 hover:border-zinc-300 hover:text-zinc-700',
                    )}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setActiveTab({
                          type: 'component',
                          componentId: component.id,
                        })
                      }
                      className="flex min-w-0 items-center gap-2"
                    >
                      <DocumentTextIcon className="h-4 w-4" />
                      <span
                        className={cn(
                          isCompactHeight
                            ? 'max-w-[160px] truncate'
                            : 'max-w-[200px] truncate',
                        )}
                      >
                        {getPackageComponentTitle(component)}
                      </span>
                    </button>
                    {canManageDocuments && (
                      <button
                        type="button"
                        aria-label={`Remove ${getPackageComponentTitle(component)}`}
                        disabled={isRemovingComponent}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleRemoveComponent(component.id).catch(() => {
                            toast.error('Failed to remove document');
                          });
                        }}
                        className={cn(
                          'rounded-full p-0.5 transition-colors',
                          isSelected
                            ? 'text-indigo-500 hover:bg-indigo-50 hover:text-indigo-700'
                            : 'text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600',
                        )}
                      >
                        <XIcon className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                );
              })}

              {canManageDocuments && (
                <button
                  type="button"
                  onClick={() => setActiveTab({ type: 'add' })}
                  className={cn(
                    '-mb-px flex items-center gap-2 border-b-2 font-medium transition-colors',
                    isCompactHeight ? 'px-2 py-1 text-[11px]' : 'px-3 py-3 text-sm',
                    activeTab.type === 'add'
                      ? 'border-indigo-600 text-indigo-700'
                      : 'border-transparent text-zinc-500 hover:border-zinc-300 hover:text-zinc-700',
                  )}
                >
                  <FilePlus2Icon className="h-4 w-4" />
                  Add Document
                </button>
              )}
            </div>

            <div
              className={cn(
                'min-h-0 flex-1 overflow-x-hidden bg-zinc-50',
                activeTab.type === 'add'
                  ? 'flex flex-col overflow-hidden'
                  : 'overflow-y-auto',
              )}
            >
              {modalBodyContent}
            </div>

            <div
              className={cn(
                'flex items-center justify-between border-t border-zinc-200 bg-white px-6',
                isCompactHeight ? 'py-2.5' : 'py-4',
              )}
            >
              {!isCompactHeight ? (
                <span className="text-sm text-zinc-500">{footerDescription}</span>
              ) : (
                <span />
              )}

              <div className="flex items-center gap-3">
                <Button
                  outline
                  onClick={handleClose}
                  className={cn(isCompactHeight && 'h-9 px-4 text-sm')}
                >
                  {activeTab.type === 'add' ? 'Cancel' : 'Done'}
                </Button>

                {activeTab.type === 'add' ? (
                  <Button
                    color="primary"
                    disabled={addActionDisabled}
                    onClick={handleAddAction}
                    className={cn(isCompactHeight && 'h-9 px-4 text-sm')}
                  >
                    {(isAddingComponent || isUploadingManualDocument) && (
                      <Loader2Icon className="mr-1.5 h-4 w-4 animate-spin" />
                    )}
                    {addActionLabel}
                  </Button>
                ) : (
                  <Button
                    color="green"
                    onClick={handleClose}
                    className={cn(isCompactHeight && 'h-9 px-4 text-sm')}
                  >
                    <CheckIcon className="mr-1.5 h-4 w-4" />
                    Done
                  </Button>
                )}
              </div>
            </div>
          </div>
        </DialogBody>
      </Dialog>

      {extractionBlob ? (
        <PdfPageExtractorModal
          isOpen={!!extractionBlob}
          onClose={() => setExtractionBlob(null)}
          matterId={matterId}
          sourceBlobId={extractionBlob.id}
          sourceUrl={extractionBlob.download_url}
          sourceName={extractionBlob.name || extractionBlob.original_filename || 'Matter document'}
          defaultCollectionId={extractionBlob.collection_id}
          context="documents"
          onExtractComplete={handleMatterBlobExtractionComplete}
          zIndex={Z_INDEX_MODAL_DEEPLY_NESTED}
          nested
        />
      ) : null}

      {primaryClient?.id ? (
        <MatterSignatureCaptureModal
          isOpen={isSignatureCaptureOpen}
          onClose={() => setIsSignatureCaptureOpen(false)}
          blob={selectedMatterBlob}
          clientId={primaryClient.id}
          onUploadComplete={() => {
            setIsSignatureCaptureOpen(false);
            refetchPackage();
          }}
        />
      ) : null}

      <Dialog
        open={isMatterDocumentPickerOpen}
        onClose={() => setIsMatterDocumentPickerOpen(false)}
        size="3xl"
        zIndex={Z_INDEX_MODAL_DEEPLY_NESTED}
        nested
      >
        <DialogBody className="p-6">
          <DialogTitle className="text-lg font-semibold text-zinc-900">
            Select matter document
          </DialogTitle>
          <div className="mt-4">
            <DocumentSelector
              matterId={matterId}
              selectedBlobIds={matterDocumentPickerSelectionIds}
              onSelectionChange={setMatterDocumentPickerSelectionIds}
              onConfirm={handleMatterDocumentPickerConfirm}
              filterBlobs={isDocumentBlob}
              multiSelect={false}
              showConfirmButton
              enableExtraction
              extractionContext="documents"
            />
          </div>
        </DialogBody>
      </Dialog>

      <Dialog
        open={isSignedPdfPickerOpen}
        onClose={() => setIsSignedPdfPickerOpen(false)}
        size="3xl"
        zIndex={Z_INDEX_MODAL_DEEPLY_NESTED}
        nested
      >
        <DialogBody className="p-6">
          <DialogTitle className="text-lg font-semibold text-zinc-900">
            Select signed authorization PDF
          </DialogTitle>
          <div className="mt-4">
            <DocumentSelector
              matterId={matterId}
              selectedBlobIds={signedPdfPickerSelectionIds}
              onSelectionChange={setSignedPdfPickerSelectionIds}
              onConfirm={handleSignedPdfPickerConfirm}
              filterBlobs={isPdfBlob}
              multiSelect={false}
              showConfirmButton
              enablePreview
              previewDialogTitle="Signed authorization preview"
              enableExtraction
              extractionContext="documents"
            />
          </div>
        </DialogBody>
      </Dialog>
    </>
  );
};


const FlowOptionButton = ({
  compact = false,
  icon: Icon,
  isSelected,
  label,
  ariaLabel,
  onClick,
}: {
  compact?: boolean;
  icon: React.ComponentType<{ className?: string }>;
  isSelected: boolean;
  label: string;
  ariaLabel?: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={ariaLabel ?? label}
    className={cn(
      'inline-flex shrink-0 items-center whitespace-nowrap border font-medium transition-colors',
      compact
        ? 'h-7 rounded-[9px] px-2 text-[11px]'
        : 'h-8 rounded-[10px] px-2 text-[11px]',
      label && (compact ? 'gap-1.5' : 'gap-1.25'),
      isSelected
        ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
        : 'border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300',
    )}
  >
    <Icon className={cn(compact ? 'h-3 w-3' : 'h-3 w-3')} />
    {label}
  </button>
);

const ModeOptionButton = ({
  compact = false,
  badge,
  isSelected,
  label,
  onClick,
  tone = 'default',
}: {
  compact?: boolean;
  badge?: string;
  isSelected: boolean;
  label: string;
  onClick: () => void;
  tone?: 'default' | 'warning';
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'inline-flex items-center gap-1.5 border font-medium transition-colors',
      compact
        ? 'h-7 rounded-[9px] px-2 text-[11px]'
        : 'h-8 rounded-[10px] px-2.5 text-[12px]',
      tone === 'warning' && isSelected && 'border-amber-300 bg-amber-50 text-amber-800',
      tone === 'warning' &&
        !isSelected &&
        'border-amber-200 bg-white text-amber-800 hover:border-amber-300',
      tone === 'default' &&
        isSelected &&
        'border-indigo-300 bg-indigo-50 text-indigo-700',
      tone === 'default' &&
        !isSelected &&
        'border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300',
    )}
  >
    {label}
    {badge && (
      <span
        className={cn(
          'rounded-full bg-white font-semibold text-current',
          compact ? 'px-1 py-0.5 text-[10px]' : 'px-1.5 py-0.5 text-[11px]',
        )}
      >
        {badge}
      </span>
    )}
  </button>
);

const LibraryPillButton = ({
  compact = false,
  isAuto,
  isSelected,
  label,
  onClick,
}: {
  compact?: boolean;
  isAuto?: boolean;
  isSelected: boolean;
  label: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'inline-flex items-center gap-1.5 border font-medium transition-colors',
      compact
        ? 'h-7 rounded-[9px] px-2 text-[11px]'
        : 'h-8 rounded-[10px] px-2.5 text-[12px]',
      isSelected
        ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
        : 'border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300',
    )}
  >
    {label}
    {isAuto && (
      <span
        className={cn(
          'rounded-full bg-white font-semibold text-indigo-700',
          compact ? 'px-1 py-0.5 text-[10px]' : 'px-1.5 py-0.5 text-[11px]',
        )}
      >
        Auto
      </span>
    )}
  </button>
);

const ToolbarGroup = ({
  compact = false,
  children,
  label,
}: {
  compact?: boolean;
  children: React.ReactNode;
  label: string;
}) => (
  <div
    className={cn(
      'inline-flex items-center gap-1.5 border border-zinc-200 bg-zinc-50/90 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]',
      compact ? 'rounded-[12px] p-1' : 'rounded-[14px] p-1.5',
    )}
  >
    <span
      className={cn(
        'inline-flex items-center font-semibold uppercase tracking-[0.08em] text-zinc-500',
        compact ? 'h-7 px-1.5 text-[10px]' : 'h-8 px-2 text-[11px]',
      )}
    >
      {label}
    </span>
    <div className="flex flex-nowrap items-center gap-1">{children}</div>
  </div>
);
